import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import ComparisonView from "./ComparisonView";
import SettingsPage from "./SettingsPage";
import VehicleChainsTable from "./components/VehicleChainsTable";

// Leaflet imports
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";

// Lucide icons
import {
  Upload,
  MapPin,
  Users,
  Bus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Play,
  X,
  Layers,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Settings,
  Building2,
  Route,
  Eye,
  Table,
  Wand2,
  Flame,
  Pencil,
  Download,
  Clock,
  Search,
  FileText,
  Lock,
  Shuffle,
  BarChart3,
  Save,
  AlertTriangle,
} from "lucide-react";

// Shadcn UI components
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { ScrollArea } from "./components/ui/scroll-area";
import { Slider } from "./components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./components/ui/tooltip";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Zone/entity ordering: numbered zones ("Zone 1" … "Zone N") first in
// strict numeric ascending order, then any custom-named entities
// alphabetically.  Mirrors the backend `_result_sort_key` (in
// routers/optimization.py + routers/export.py) and `_zone_sort_key` (in
// routers/data.py) so See-Routes, Assign-Buses, sidebar, and Excel
// export all share the same order.
const _zoneNumberFromName = (name) => {
  const m = String(name || "").trim().match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : null;
};
const zoneEntitySortComparator = (a, b) => {
  const na = _zoneNumberFromName(a?.entity_name);
  const nb = _zoneNumberFromName(b?.entity_name);
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  return String(a?.entity_name || "").toLowerCase().localeCompare(String(b?.entity_name || "").toLowerCase());
};


// ─── BUID (Client Key) helpers ────────────────────────────────────────────────
// Per-tab BUID via sessionStorage, with localStorage as the cross-tab default.
// Why: localStorage is shared across all tabs of the same origin. If we used
// localStorage alone, switching BUID in tab 2 would silently change tab 1's
// API calls on the next poll — phantom-tenant bug. sessionStorage is per-tab,
// survives refreshes, and is invisible to siblings.
//
// Read order:  sessionStorage  →  localStorage  →  prompt user
// Write:       both, so a brand-new tab inherits the most-recent choice
//              as its starting default but can independently switch.
// ── BUID resolution (per-tab via sessionStorage) ─────────────────────────────
// DESIGN RULE: each browser tab is a separate tenant workspace. sessionStorage
// is tab-scoped; localStorage is shared across all tabs in the browser.
//
// `getBUID()` — the ONLY source of truth for which BUID this tab is operating
//               on. Reads sessionStorage. Never falls back to localStorage,
//               otherwise a fresh Tab 2 would silently inherit Tab 1's BUID
//               and both tabs would end up sending the same X-BUID header,
//               serializing their uploads through the per-BUID task lock.
//
// `getLastBUID()` — convenience read of localStorage. Used ONLY to pre-fill
//                   the first-load modal so returning users don't have to
//                   retype their BUID. Never used for request routing.
//
// `setBUID(v)` — writes BOTH: sessionStorage pins this tab to v, and
//                localStorage records v as the suggested default for any
//                *future new tab*. Existing tabs are unaffected (they read
//                their own sessionStorage).
const BUID_STORAGE_KEY = "routeoptima_buid";

const getBUID = () => sessionStorage.getItem(BUID_STORAGE_KEY) || "";

const getLastBUID = () => localStorage.getItem(BUID_STORAGE_KEY) || "";

const setBUID = (v) => {
  sessionStorage.setItem(BUID_STORAGE_KEY, v);
  localStorage.setItem(BUID_STORAGE_KEY, v);
};

// Attach X-BUID to every axios request automatically
axios.interceptors.request.use((config) => {
  const buid = getBUID();
  if (buid) config.headers["X-BUID"] = buid;
  return config;
});

// Group colors
const GROUP_COLORS = [
  "#EF4444", "#F97316", "#F59E0B", "#84CC16", "#10B981",
  "#06B6D4", "#3B82F6", "#6366F1", "#8B5CF6", "#D946EF"
];

// Create custom marker icon
const createMarkerIcon = (color, isSelected = false, label = "") => {
  const size = isSelected ? 32 : 28;
  const borderWidth = isSelected ? 3 : 2;
  
  return L.divIcon({
    className: "custom-div-icon",
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: 50%;
        border: ${borderWidth}px solid ${isSelected ? "#0F172A" : "white"};
        box-shadow: ${isSelected ? "0 0 0 2px white, 0 4px 8px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.2)"};
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        font-weight: 600;
        color: white;
        transition: all 0.2s;
      ">${label}</div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Create office marker icon (larger, different style)
const createOfficeMarkerIcon = () => {
  return L.divIcon({
    className: "custom-div-icon office-marker",
    html: `
      <div style="
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #1E40AF, #3B82F6);
        border-radius: 8px;
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(30, 64, 175, 0.4), 0 0 0 3px rgba(59, 130, 246, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        transform: rotate(45deg);
      ">
        <svg style="transform: rotate(-45deg);" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
          <path d="M10 6h4"/>
          <path d="M10 10h4"/>
          <path d="M10 14h4"/>
          <path d="M10 18h4"/>
        </svg>
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
};

// Map bounds updater component
function MapBoundsUpdater({ stops }) {
  const map = useMap();
  
  useEffect(() => {
    if (stops.length > 0) {
      const bounds = L.latLngBounds(stops.map((s) => [s.latitude, s.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [stops, map]);
  
  return null;
}

function FocusedRouteFitter({ route, officeStop }) {
  const map = useMap();
  useEffect(() => {
    if (route && route.route.stops?.length > 0) {
      const points = route.route.stops.map(s => [s.latitude, s.longitude]);
      if (officeStop) points.push([officeStop.latitude, officeStop.longitude]);
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, [route, officeStop, map]);
  return null;
}



// Route polyline component that uses actual road geometry
function ActualRoutePolyline({ route, routeGeometry, entityId, color }) {
  // Convert OSRM coordinates [lng, lat] to Leaflet [lat, lng]
  const positions = routeGeometry?.coordinates?.map(coord => [coord[1], coord[0]]) || [];
  
  if (positions.length === 0) return null;
  
  return (
    <Polyline
      positions={positions}
      color={color}
      weight={4}
      opacity={0.85}
      dashArray={route.route_type === "shared" ? "12, 6" : null}
    >
      <Popup>
        <div className="text-sm">
          <div className="font-semibold">{route.bus_id}</div>
          <div className="text-xs text-muted-foreground">
            Capacity: {route.capacity} | Passengers: {route.total_passengers}
          </div>
          <div className="text-xs mt-1">
            Stops: {route.stops.map(s => s.stop_name).join(" → ")}
          </div>
          {routeGeometry?.distance && (
            <div className="text-xs mt-1 text-blue-600">
              Distance: {(routeGeometry.distance / 1000).toFixed(1)} km
            </div>
          )}
        </div>
      </Popup>
    </Polyline>
  );
}

// Heatmap layer component - uses sized translucent circles (same color, size = demand)
function HeatmapLayer({ stops, shiftFilter, visible }) {
  const map = useMap();
  
  useEffect(() => {
    if (!visible || !stops || stops.length === 0) {
      // Remove existing heatmap circles
      map.eachLayer((layer) => {
        if (layer.options?.className === 'heatmap-circle') {
          map.removeLayer(layer);
        }
      });
      return;
    }
    
    // Remove existing heatmap circles first
    map.eachLayer((layer) => {
      if (layer.options?.className === 'heatmap-circle') {
        map.removeLayer(layer);
      }
    });
    
    // Get demand based on shift filter - uses dynamic shift_demands dict
    const getDemand = (stop) => {
      const sd = stop.shift_demands || {};
      if (shiftFilter === 'peak') {
        const vals = Object.values(sd);
        return vals.length > 0 ? Math.max(...vals) : 0;
      }
      return sd[shiftFilter] || 0;
    };
    
    // Find max demand for scaling
    const maxDemand = Math.max(...stops.map(getDemand), 1);
    
    // Single color for all circles - purple (distinct from all group colors)
    const heatmapColor = '#8B5CF6';
    
    // Create circles for each stop - add to pane to render below markers
    stops.forEach((stop) => {
      const demand = getDemand(stop);
      if (demand <= 0) return;
      
      const intensity = demand / maxDemand;
      
      // Circle radius in meters: scale based on demand
      // Min: 800m, Max: 4000m for good visibility
      const minRadius = 800;
      const maxRadius = 4000;
      const radius = minRadius + (intensity * (maxRadius - minRadius));
      
      const circle = L.circle([stop.latitude, stop.longitude], {
        radius: radius,
        color: heatmapColor,
        fillColor: heatmapColor,
        fillOpacity: 0.35,
        weight: 2,
        opacity: 0.7,
        className: 'heatmap-circle',
        interactive: true
      });
      
      circle.bindTooltip(`${stop.stop_name}<br/>Demand: ${demand}`, {
        permanent: false,
        direction: 'top',
        className: 'heatmap-tooltip'
      });
      
      // Add to map (will render below markers due to default z-index)
      circle.addTo(map);
      circle.bringToBack();
    });
    
    return () => {
      map.eachLayer((layer) => {
        if (layer.options?.className === 'heatmap-circle') {
          map.removeLayer(layer);
        }
      });
    };
  }, [map, stops, shiftFilter, visible]);
  
  return null;
}

// Map search control - searches stops and pans to selected one
function MapSearchControl({ stops }) {
  const map = useMap();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      return;
    }
    const q = query.toLowerCase();
    setResults(stops.filter((s) => s.stop_name.toLowerCase().includes(q)).slice(0, 8));
  }, [query, stops]);

  const handleSelect = (stop) => {
    map.flyTo([stop.latitude, stop.longitude], 15, { duration: 0.8 });
    // Open popup for the marker after flying
    setTimeout(() => {
      map.eachLayer((layer) => {
        if (layer.getLatLng) {
          const ll = layer.getLatLng();
          if (
            Math.abs(ll.lat - stop.latitude) < 0.0001 &&
            Math.abs(ll.lng - stop.longitude) < 0.0001
          ) {
            layer.openPopup();
          }
        }
      });
    }, 900);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div
      className="leaflet-top leaflet-right"
      style={{ pointerEvents: "auto", zIndex: 1000 }}
    >
      <div
        className="leaflet-control"
        style={{ margin: "10px", pointerEvents: "auto" }}
      >
        <div className="relative" data-testid="map-search-control">
          <div className="flex items-center bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden"
               style={{ width: open ? 260 : 40, transition: "width 0.2s ease" }}>
            {open && (
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stops..."
                className="flex-1 px-3 py-2 text-sm outline-none bg-transparent"
                data-testid="map-search-input"
                autoFocus
              />
            )}
            <button
              onClick={() => {
                setOpen((v) => {
                  if (!v) setTimeout(() => inputRef.current?.focus(), 100);
                  else { setQuery(""); setResults([]); }
                  return !v;
                });
              }}
              className="flex items-center justify-center w-10 h-10 hover:bg-slate-50 transition-colors shrink-0"
              data-testid="map-search-toggle"
            >
              {open ? <X className="w-4 h-4 text-slate-500" /> : <Search className="w-4 h-4 text-slate-500" />}
            </button>
          </div>
          {open && results.length > 0 && (
            <div className="absolute right-0 mt-1 w-[260px] bg-white rounded-lg shadow-lg border border-slate-200 max-h-[240px] overflow-auto"
                 data-testid="map-search-results">
              {results.map((stop) => (
                <button
                  key={stop.id}
                  onClick={() => handleSelect(stop)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2"
                  data-testid={`search-result-${stop.id}`}
                >
                  <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="truncate">{stop.stop_name}</span>
                </button>
              ))}
            </div>
          )}
          {open && query.trim().length > 0 && results.length === 0 && (
            <div className="absolute right-0 mt-1 w-[260px] bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-3 text-sm text-muted-foreground text-center">
              No stops found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Routes modal with shift tabs
function RoutesModalContent({ selectedRouteEntity, setShowRoutesPanel, setSelectedRouteEntity }) {
  const deployments = selectedRouteEntity?.shift_deployments || {};
  const hasShiftData = Object.keys(deployments).length > 0;
  const shiftKeys = Object.keys(deployments).sort();
  const vehicleSchedule = selectedRouteEntity?.vehicle_schedule || {};
  const vehicles = vehicleSchedule.vehicles || [];
  
  // Determine peak shift from shift_demands
  const sd = selectedRouteEntity?.shift_demands || {};
  const peakShift = Object.keys(sd).length > 0 
    ? Object.entries(sd).reduce((a, b) => b[1] > a[1] ? b : a, ['', 0])[0]
    : (shiftKeys[0] || "");
  
  const renderBusTable = (busRoutes, shiftKey) => {
    if (!busRoutes || busRoutes.length === 0) {
      return <div className="text-center py-8 text-muted-foreground">No routes for this shift.</div>;
    }
    
    const activeRoutes = busRoutes.filter(r => r.status !== "Idle" && r.total_passengers > 0);
    const idleRoutes = busRoutes.filter(r => r.status === "Idle" || r.total_passengers === 0);
    const shiftDemand = deployments[shiftKey]?.demand || 0;
    
    return (
      <div>
        <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
          <span>Demand: <strong className="text-foreground">{shiftDemand}</strong></span>
          <span>Buses: <strong className="text-foreground">{activeRoutes.length}</strong></span>
          {idleRoutes.length > 0 && (
            <span className="text-amber-600">Idle: <strong>{idleRoutes.length}</strong></span>
          )}
        </div>
        <table className="results-table w-full" data-testid={`shift-${shiftKey}-table`}>
          <thead>
            <tr>
              <th>Bus ID</th>
              <th>Cap</th>
              <th>Stops & Pickup</th>
              <th>Pax</th>
              <th>Occupancy</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {busRoutes.map((route, idx) => (
              <tr key={route.bus_id || idx} className={route.status === "Idle" || route.total_passengers === 0 ? "opacity-50" : ""}>
                <td className="font-mono font-semibold text-sm">{route.bus_id}</td>
                <td className="font-mono text-sm">{route.capacity}</td>
                <td>
                  {route.stops && route.stops.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {route.stops.map((stop, i) => (
                        <div key={i} className="text-xs bg-slate-100 px-2 py-1 rounded">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{stop.stop_name}</span>
                            <span className="text-muted-foreground">({stop.passengers} pax)</span>
                          </div>
                          {stop.pickup_times && stop.pickup_times[shiftKey] && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              <span className="bg-slate-200 px-1 rounded">Pickup: {stop.pickup_times[shiftKey]}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Idle</span>
                  )}
                </td>
                <td className="font-mono text-sm">{route.total_passengers}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <div className="w-12 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${route.utilization}%` }} />
                    </div>
                    <span className="font-mono text-[10px]">{route.utilization}%</span>
                  </div>
                </td>
                <td>
                  <Badge variant={route.status === "Idle" || route.total_passengers === 0 ? "outline" : "default"} className="text-[10px]">
                    {route.status === "Idle" || route.total_passengers === 0 ? "Idle" : "Active"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVehiclesTab = () => {
    if (!vehicles.length) {
      return <div className="text-center py-8 text-muted-foreground">No vehicle schedule available.</div>;
    }
    // Group vehicles by bus_id (route)
    const byRoute = {};
    vehicles.forEach(v => {
      if (!byRoute[v.bus_id]) byRoute[v.bus_id] = [];
      byRoute[v.bus_id].push(v);
    });

    return (
      <div>
        <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
          <span>Routes: <strong className="text-foreground">{Object.keys(byRoute).length}</strong></span>
          <span>Vehicles: <strong className="text-foreground">{vehicles.length}</strong></span>
        </div>
        <table className="results-table w-full" data-testid="vehicles-schedule-table">
          <thead>
            <tr>
              <th>Vehicle</th>
              <th>Route</th>
              <th>Cap</th>
              <th>Trips</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byRoute).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })).map(([busId, vList]) => (
              vList.map((v, vi) => (
                <tr key={v.vehicle_id} className={vi === 0 ? "border-t-2 border-slate-300" : ""}>
                  <td className="font-mono font-semibold text-sm">{v.vehicle_id}</td>
                  <td className="font-mono text-sm">{v.bus_id}</td>
                  <td className="font-mono text-sm">
                    {v.original_capacity && v.original_capacity !== v.capacity ? (
                      <span className="text-emerald-700" title={`Right-sized: max ${v.required_pax} pax on board`}>
                        {v.original_capacity}→{v.capacity}
                      </span>
                    ) : v.capacity}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {v.trips.map((t, ti) => (
                        <span key={ti} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          t.direction === 'LOGIN'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {t.shift} {t.direction === 'LOGIN' ? 'In' : 'Out'}
                        </span>
                      ))}
                      {v.trips.length === 0 && (
                        <span className="text-xs text-muted-foreground italic">Idle</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div className="overflow-auto max-h-[60vh]">
        {hasShiftData ? (
          <Tabs defaultValue={peakShift} data-testid="shift-tabs">
            <TabsList className="mb-3">
              {shiftKeys.map(s => (
                <TabsTrigger key={s} value={s} data-testid={`shift-tab-${s}`}>
                  Shift {s} {s === peakShift ? " (Peak)" : ""}
                </TabsTrigger>
              ))}
              {vehicles.length > 0 && (
                <TabsTrigger value="__vehicles" data-testid="shift-tab-vehicles">
                  Vehicles ({vehicleSchedule.total_vehicles || vehicles.length})
                </TabsTrigger>
              )}
            </TabsList>
            {shiftKeys.map(s => (
              <TabsContent key={s} value={s}>
                {renderBusTable(deployments[s]?.bus_routes || [], s)}
              </TabsContent>
            ))}
            {vehicles.length > 0 && (
              <TabsContent value="__vehicles">
                {renderVehiclesTab()}
              </TabsContent>
            )}
          </Tabs>
        ) : selectedRouteEntity?.bus_routes?.length > 0 ? (
          <table className="results-table w-full">
            <thead>
              <tr>
                <th>Bus ID</th>
                <th>Cap</th>
                <th>Stops & Pickup Times</th>
                <th>Pax</th>
                <th>Occupancy</th>
              </tr>
            </thead>
            <tbody>
              {selectedRouteEntity.bus_routes.map((route, idx) => (
                <tr key={route.bus_id || idx}>
                  <td className="font-mono font-semibold">{route.bus_id}</td>
                  <td className="font-mono">{route.capacity}</td>
                  <td>
                    <div className="flex flex-col gap-1">
                      {route.stops.map((stop, i) => (
                        <div key={i} className="text-xs bg-slate-100 px-2 py-1 rounded">
                          <span className="font-medium">{stop.stop_name}</span>
                          <span className="text-muted-foreground ml-1">({stop.passengers} pax)</span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="font-mono">{route.total_passengers}</td>
                  <td className="font-mono text-xs">{route.utilization}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-muted-foreground">No routes available.</div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setShowRoutesPanel(false)}>Close</Button>
        {selectedRouteEntity && (
          <Button onClick={() => { setShowRoutesPanel(false); setSelectedRouteEntity(selectedRouteEntity); }}>
            <Route className="w-4 h-4 mr-2" />
            Show on Map
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

// Assign Buses Modal
function AssignBusesModal({ open, onClose, optimizationResults, stops, individualVehicles, assignments, setAssignments }) {
  
  // Only initialize assignments when modal opens AND assignments are empty
  useEffect(() => {
    if (!open || !optimizationResults || assignments.length > 0) return;
    
    // Build stop preferred_vendor lookup
    const vendorMap = {};
    stops.forEach(s => {
      if (s.preferred_vendor) vendorMap[s.stop_name] = s.preferred_vendor;
    });
    
    // Build vendor matching: map preferred vendor text to actual vehicle vendor
    const vehicleVendors = [...new Set(individualVehicles.filter(v => v.vendor).map(v => v.vendor))];
    const matchVendor = (preferred) => {
      if (!preferred) return "";
      const pLower = preferred.toLowerCase();
      const exact = vehicleVendors.find(v => v.toLowerCase() === pLower);
      if (exact) return exact;
      const partial = vehicleVendors.find(v => 
        v.toLowerCase().includes(pLower) || pLower.includes(v.toLowerCase())
      );
      if (partial) return partial;
      return "";
    };
    
    // Collect all shift keys
    const allShiftKeys = new Set();
    optimizationResults.results.forEach(r => {
      Object.keys(r.shift_deployments || {}).forEach(k => allShiftKeys.add(k));
    });
    const sortedShiftKeys = [...allShiftKeys].sort();
    
    // Build rows from vehicle_schedule instead of bus_routes
    const rows = [];
    optimizationResults.results.forEach(result => {
      const sd = result.shift_deployments || {};
      const vs = result.vehicle_schedule || {};
      const vehicleList = vs.vehicles || [];
      
      if (vehicleList.length === 0) return;
      
      vehicleList.forEach(vehicle => {
        // Build shift trip cells: for each shift, login and logout
        const shiftTrips = {};
        const stopTotals = {};
        
        sortedShiftKeys.forEach(sk => {
          const shiftRoutes = sd[sk]?.bus_routes || [];
          const busDep = shiftRoutes.find(r => r.bus_id === vehicle.bus_id);
          const hasActiveRoute = busDep && busDep.status !== 'Idle' && busDep.total_passengers > 0;
          const stopsStr = hasActiveRoute 
            ? busDep.stops.map(s => `${s.stop_name}(${s.passengers})`).join(", ")
            : "";
          
          // Track stop totals for vendor matching
          if (hasActiveRoute) {
            busDep.stops.forEach(s => {
              if (!stopTotals[s.stop_name]) stopTotals[s.stop_name] = 0;
              stopTotals[s.stop_name] += (s.passengers || 0);
            });
          }
          
          // LOGIN cell
          const loginKey = `${sk}-LOGIN`;
          const logoutKey = `${sk}-LOGOUT`;
          
          // Use backend-computed shift_status: "active", "busy", or "idle"
          // Fallback: if shift_status doesn't have this key but route has demand, show stops
          const loginStatus = vehicle.shift_status?.[loginKey];
          const logoutStatus = vehicle.shift_status?.[logoutKey];
          
          const loginCell = loginStatus === "active" ? stopsStr 
            : loginStatus === "busy" ? "Busy" 
            : loginStatus === "idle" ? "Idle"
            : (hasActiveRoute ? stopsStr : "Idle");
          
          const logoutCell = logoutStatus === "active" ? stopsStr 
            : logoutStatus === "busy" ? "Busy" 
            : logoutStatus === "idle" ? "Idle"
            : (hasActiveRoute ? stopsStr : "Idle");
          
          shiftTrips[sk] = {
            login: loginCell,
            logout: logoutCell,
          };
        });
        
        // Find stop with max total employees for vendor matching
        let maxStop = "";
        let maxTotal = 0;
        Object.entries(stopTotals).forEach(([sn, total]) => {
          if (total > maxTotal) { maxTotal = total; maxStop = sn; }
        });
        
        const autoVendor = matchVendor(vendorMap[maxStop] || "");
        
        rows.push({
          vehicle_id: vehicle.vehicle_id,
          bus_id: vehicle.bus_id,
          capacity: vehicle.capacity,
          entity_name: result.entity_name,
          vendor: autoVendor,
          vehicle_no: "",
          shiftTrips,
          maxStop,
        });
      });
    });
    
    setAssignments(rows);
  }, [open, optimizationResults, stops]);
  
  const updateVendor = (idx, vendor) => {
    setAssignments(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], vendor, vehicle_no: "" };
      return next;
    });
  };
  
  const updateVehicleNo = (idx, vehicle_no) => {
    setAssignments(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], vehicle_no };
      return next;
    });
  };
  
  // Get unique vendors from individual vehicles
  const allVendors = [...new Set(individualVehicles.filter(v => v.vendor).map(v => v.vendor))].sort();
  
  // Get vehicles filtered by vendor + capacity (>= needed), excluding already assigned
  const getAvailableVehicles = (vendor, capacity, currentVehicleNo, searchText = "") => {
    const assignedNos = new Set(assignments.map(a => a.vehicle_no).filter(Boolean));
    return individualVehicles.filter(v => {
      if (v.capacity < capacity) return false;
      if (vendor && v.vendor !== vendor) return false;
      if (assignedNos.has(v.vehicle_no) && v.vehicle_no !== currentVehicleNo) return false;
      if (searchText && !v.vehicle_no.toLowerCase().includes(searchText.toLowerCase())) return false;
      return true;
    });
  };
  
  // Track search text per vehicle row
  const [vehicleSearch, setVehicleSearch] = useState({});
  
  // Collect all shift keys for header
  const allShiftKeys = (() => {
    const keys = new Set();
    optimizationResults?.results?.forEach(r => {
      Object.keys(r.shift_deployments || {}).forEach(k => keys.add(k));
    });
    return [...keys].sort();
  })();
  
  // Group assignments by bus_id for visual grouping
  const groupedRows = [];
  let lastBusId = null;
  assignments.forEach((row, idx) => {
    groupedRows.push({ ...row, idx, isFirstInRoute: row.bus_id !== lastBusId });
    lastBusId = row.bus_id;
  });
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="z-[9999] max-w-[95vw] max-h-[85vh] overflow-hidden" style={{ zIndex: 9999 }} data-testid="assign-buses-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5" />
            Assign Vehicles
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-auto max-h-[68vh]">
          <table className="w-full text-sm border-collapse" data-testid="assign-buses-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left p-2 font-semibold text-xs whitespace-nowrap" rowSpan={2}>Vehicle</th>
                <th className="text-left p-2 font-semibold text-xs whitespace-nowrap" rowSpan={2}>Zone</th>
                <th className="text-center p-2 font-semibold text-xs whitespace-nowrap" rowSpan={2}>Cap</th>
                <th className="text-left p-2 font-semibold text-xs whitespace-nowrap min-w-[140px]" rowSpan={2}>Vendor</th>
                <th className="text-left p-2 font-semibold text-xs whitespace-nowrap min-w-[180px]" rowSpan={2}>Bus No.</th>
                {allShiftKeys.map(sk => (
                  <th key={sk} className="text-center p-1 font-semibold text-xs border-l border-slate-200" colSpan={2}>
                    Shift {sk}
                  </th>
                ))}
              </tr>
              <tr className="border-b-2 border-slate-300">
                {allShiftKeys.map(sk => (
                  <React.Fragment key={sk}>
                    <th className="text-center p-1 font-medium text-[10px] text-muted-foreground border-l border-slate-200 whitespace-nowrap">In</th>
                    <th className="text-center p-1 font-medium text-[10px] text-muted-foreground whitespace-nowrap">Out</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupedRows.map((row) => {
                const availableVehicles = getAvailableVehicles(row.vendor, row.capacity, row.vehicle_no, vehicleSearch[row.vehicle_id] || "");
                return (
                  <tr 
                    key={row.vehicle_id} 
                    className={`border-b border-slate-100 hover:bg-slate-50 ${row.isFirstInRoute ? "border-t-2 border-t-slate-300" : ""}`}
                    data-testid={`assign-row-${row.vehicle_id}`}
                  >
                    <td className="p-2 font-mono font-semibold text-sm whitespace-nowrap">{row.vehicle_id}</td>
                    <td className="p-2 text-xs text-muted-foreground">{row.entity_name}</td>
                    <td className="p-2 text-center font-mono text-sm">{row.capacity}</td>
                    <td className="p-2">
                      <select
                        value={row.vendor}
                        onChange={(e) => updateVendor(row.idx, e.target.value)}
                        className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-blue-400 outline-none"
                        data-testid={`vendor-select-${row.vehicle_id}`}
                      >
                        <option value="">Any vendor</option>
                        {allVendors.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={row.vehicle_no || "Search bus..."}
                          value={vehicleSearch[row.vehicle_id] ?? ""}
                          onChange={(e) => {
                            setVehicleSearch(prev => ({ ...prev, [row.vehicle_id]: e.target.value }));
                            if (row.vehicle_no) updateVehicleNo(row.idx, "");
                          }}
                          onFocus={() => setVehicleSearch(prev => ({ ...prev, [row.vehicle_id]: prev[row.vehicle_id] ?? "" }))}
                          onBlur={() => setTimeout(() => setVehicleSearch(prev => { const n = {...prev}; delete n[row.vehicle_id]; return n; }), 200)}
                          className={`w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-blue-400 outline-none ${row.vehicle_no ? "font-semibold text-slate-800" : ""}`}
                          data-testid={`vehicle-search-${row.vehicle_id}`}
                        />
                        {vehicleSearch[row.vehicle_id] !== undefined && (
                          <div className="absolute z-50 top-full left-0 w-full bg-white border border-slate-200 rounded shadow-lg max-h-40 overflow-auto mt-0.5">
                            {availableVehicles.length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-muted-foreground">No matches</div>
                            ) : (
                              availableVehicles.map(v => (
                                <div
                                  key={v.vehicle_no}
                                  className="px-2 py-1.5 text-xs cursor-pointer hover:bg-blue-50"
                                  onMouseDown={() => {
                                    updateVehicleNo(row.idx, v.vehicle_no);
                                    setVehicleSearch(prev => { const n = {...prev}; delete n[row.vehicle_id]; return n; });
                                  }}
                                >
                                  {v.vehicle_no} ({v.capacity}s)
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    {allShiftKeys.map(sk => {
                      const trip = row.shiftTrips?.[sk] || {};
                      return (
                        <React.Fragment key={sk}>
                          <td className="p-1 text-xs border-l border-slate-200">
                            {trip.login === "Busy" ? (
                              <span className="text-red-500 font-medium italic text-[10px]">Busy</span>
                            ) : trip.login === "Idle" ? (
                              <span className="text-amber-500 italic text-[10px]">Idle</span>
                            ) : trip.login ? (
                              <span className="text-[10px]">{trip.login}</span>
                            ) : null}
                          </td>
                          <td className="p-1 text-xs">
                            {trip.logout === "Busy" ? (
                              <span className="text-red-500 font-medium italic text-[10px]">Busy</span>
                            ) : trip.logout === "Idle" ? (
                              <span className="text-amber-500 italic text-[10px]">Idle</span>
                            ) : trip.logout ? (
                              <span className="text-[10px]">{trip.logout}</span>
                            ) : null}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <div className="text-xs text-muted-foreground mr-auto">
            {assignments.filter(a => a.vehicle_no).length} / {assignments.length} vehicles assigned
          </div>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button
            data-testid="save-assignments-btn"
            disabled={assignments.filter(a => a.vehicle_no).length === 0}
            onClick={async () => {
              try {
                const imeiMap = {};
                individualVehicles.forEach(v => { if (v.vehicle_no) imeiMap[v.vehicle_no] = v.imei || ""; });

                const routes = [];
                assignments.forEach(row => {
                  if (!row.vehicle_no) return;
                  const imei = imeiMap[row.vehicle_no] || "";

                  optimizationResults.results?.forEach(result => {
                    const zone = result.entity_name || "";
                    const sd = result.shift_deployments || {};
                    
                    // Use dynamic shift keys
                    Object.keys(sd).sort().forEach(sk => {
                      const shiftRoutes = sd[sk]?.bus_routes || [];
                      const busDep = shiftRoutes.find(r => r.bus_id === row.bus_id);
                      if (busDep && busDep.stops?.length > 0) {
                        const routeStops = busDep.stops.map(s => {
                          const stopData = stops.find(st => st.stop_name === s.stop_name);
                          return {
                            name: s.stop_name,
                            latitude: stopData?.latitude || 0,
                            longitude: stopData?.longitude || 0,
                            time: s.pickup_time || s.dropoff_time || "",
                            passengers: s.passengers || 0,
                          };
                        });
                        
                        // Only create deployment for trips this vehicle actually does
                        const trip = row.shiftTrips?.[sk] || {};
                        if (trip.login && trip.login !== "Busy") {
                          routes.push({
                            vehicle_id: row.vehicle_id,
                            bus_id: row.bus_id,
                            vehicle_no: row.vehicle_no,
                            imei,
                            zone,
                            shift: sk,
                            direction: "LOGIN",
                            stops: routeStops,
                          });
                        }
                        if (trip.logout && trip.logout !== "Busy") {
                          routes.push({
                            vehicle_id: row.vehicle_id,
                            bus_id: row.bus_id,
                            vehicle_no: row.vehicle_no,
                            imei,
                            zone,
                            shift: sk,
                            direction: "LOGOUT",
                            stops: routeStops,
                          });
                        }
                      }
                    });
                  });
                });

                // Build ALL optimization routes (for Vehicle Tracking to show undeployed routes too)
                const allRoutes = [];
                optimizationResults.results?.forEach(result => {
                  const zone = result.entity_name || "";
                  const sd = result.shift_deployments || {};
                  Object.keys(sd).sort().forEach(sk => {
                    const shiftRoutes = sd[sk]?.bus_routes || [];
                    shiftRoutes.forEach(br => {
                      if (!br.stops?.length) return;
                      const routeStops = br.stops.map(s => {
                        const stopData = stops.find(st => st.stop_name === s.stop_name);
                        return {
                          name: s.stop_name,
                          latitude: stopData?.latitude || s.latitude || 0,
                          longitude: stopData?.longitude || s.longitude || 0,
                          time: s.pickup_time || s.dropoff_time || "",
                          passengers: s.passengers || 0,
                        };
                      });
                      ["LOGIN", "LOGOUT"].forEach(dir => {
                        allRoutes.push({
                          bus_id: br.bus_id,
                          zone,
                          shift: sk,
                          direction: dir,
                          stops: routeStops,
                        });
                      });
                    });
                  });
                });

                const res = await axios.post(`${API}/vehicle-tracking/save-deployment`, { routes, all_routes: allRoutes });
                toast.success(`Saved ${res.data.count} deployment routes`);
                onClose(false);
              } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to save");
              }
            }}
          >
            Save & Deploy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// File upload component
function FileUpload({ label, endpoint, icon: Icon, onSuccess, templateEndpoint, downloadEndpoint, hasData, disabled = false, disabledReason = "" }) {
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (disabled) {
      toast.error(disabledReason || "A task is currently running. Please wait.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}${endpoint}`, formData);
      toast.success(response.data.message);
      setSuccess(true);
      onSuccess?.(response.data);
    } catch (error) {
      console.error("Upload error:", error.message, error.response?.status, error.response?.data);
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (detail) {
        toast.error(`Upload failed: ${detail}`);
      } else if (status) {
        toast.error(`Upload failed (${status}): ${error.response?.statusText || "Server error"}`);
      } else {
        toast.error(`Upload failed: ${error.message || "Network error"}`);
      }
      setSuccess(false);
    } finally {
      setUploading(false);
      // Reset file input so same file can be re-uploaded
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = (e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = `${API}${templateEndpoint}`;
    link.download = `template_${endpoint.replace("/upload/", "")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadUploaded = (e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = `${API}${downloadEndpoint}`;
    link.download = `uploaded_${endpoint.replace("/upload/", "")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={`upload-area ${success ? "success" : ""} ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      data-testid={`upload-${endpoint.replace("/upload/", "")}`}
      title={disabled ? disabledReason : ""}
    >
      <div
        className="cursor-pointer"
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleUpload}
          disabled={disabled}
          className="hidden"
        />
        <div className="flex items-center justify-center gap-2 mb-1">
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : success ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <Icon className="w-4 h-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {uploading ? "Uploading..." : success ? "Uploaded" : "Click to upload .xlsx"}
        </p>
      </div>
      <div className="flex items-center justify-center gap-2 mt-1.5 pt-1.5 border-t border-border/40">
        <button
          onClick={handleDownloadTemplate}
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
          data-testid={`template-${endpoint.replace("/upload/", "")}`}
        >
          <FileSpreadsheet className="w-3 h-3" />
          Template
        </button>
        {(success || hasData) && (
          <>
            <span className="text-muted-foreground/30 text-[10px]">|</span>
            <button
              onClick={handleDownloadUploaded}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
              data-testid={`download-${endpoint.replace("/upload/", "")}`}
            >
              <Download className="w-3 h-3" />
              Download
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// GPS Simulation Component
function GpsSimulation() {
  const [simStatus, setSimStatus] = useState(null); // null | {status, progress, ...}
  const [polling, setPolling] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Check status on mount
  useEffect(() => {
    axios.get(`${API}/gps-simulation/status`).then(res => {
      if (res.data?.status && res.data.status !== "idle") {
        setSimStatus(res.data);
        if (res.data.status === "running") setPolling(true);
      }
    }).catch(() => {});
  }, [API]);

  // Poll while running
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/gps-simulation/status`);
        setSimStatus(res.data);
        if (res.data.status !== "running") {
          setPolling(false);
          clearInterval(interval);
        }
      } catch (e) { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [polling, API]);

  const handleStart = async () => {
    try {
      await axios.post(`${API}/gps-simulation/start`);
      setSimStatus({ status: "running", progress: 0, total: 0, completed: 0 });
      setPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start simulation");
    }
  };

  const handleDownload = () => {
    window.open(`${API}/download/roster-gps-simulation`, "_blank");
  };

  if (!simStatus || simStatus.status === "idle") {
    return (
      <Button variant="outline" size="sm" className="w-full h-7 text-[11px]" onClick={handleStart} data-testid="gps-sim-start-btn">
        <MapPin className="w-3 h-3 mr-1" />
        Run GPS Geocode Simulation
      </Button>
    );
  }

  if (simStatus.status === "running") {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-2 space-y-1.5">
        <div className="flex items-center gap-2 text-xs text-blue-700">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Geocoding nodal points... {simStatus.progress}%
        </div>
        <div className="w-full bg-blue-100 rounded-full h-1.5">
          <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${simStatus.progress}%` }} />
        </div>
        <div className="text-[10px] text-blue-500">{simStatus.completed} / {simStatus.total} unique nodal points</div>
      </div>
    );
  }

  if (simStatus.status === "complete") {
    return (
      <div className="space-y-1.5">
        <div className="rounded-md border border-green-200 bg-green-50 p-2">
          <div className="text-[11px] text-green-700 font-medium">GPS Simulation Complete</div>
          <div className="text-[10px] text-green-600">
            {simStatus.total - (simStatus.not_found || 0)} geocoded, {simStatus.not_found || 0} not found
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full h-7 text-[11px]" onClick={handleDownload} data-testid="gps-sim-download-btn">
          <Download className="w-3 h-3 mr-1" />
          Download GPS Simulation
        </Button>
      </div>
    );
  }

  if (simStatus.status === "error") {
    return (
      <div className="space-y-1.5">
        <div className="text-[11px] text-red-600">Simulation failed: {simStatus.error}</div>
        <Button variant="outline" size="sm" className="w-full h-7 text-[11px]" onClick={handleStart}>Retry</Button>
      </div>
    );
  }

  return null;
}


// Main App Component
function App() {
  // ─── BUID state ────────────────────────────────────────────────────────────
  // `buid` / `showBuidModal` are driven by sessionStorage only (per-tab).
  // `buidInput` is pre-filled with the last-used BUID from localStorage purely
  // as a typing convenience — selecting the prefill still requires the user
  // to explicitly Continue, so each tab makes a deliberate BUID choice.
  const [buid, setBuid] = useState(() => getBUID());
  const [showBuidModal, setShowBuidModal] = useState(() => !getBUID());
  const [buidInput, setBuidInput] = useState(() => getBUID() || getLastBUID());
  const [showBuidSwitcher, setShowBuidSwitcher] = useState(false);

  // Data state
  const [stops, setStops] = useState([]);
  const [groups, setGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [officeStop, setOfficeStop] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);

  // View state
  const [currentView, setCurrentView] = useState("planning"); // 'planning' | 'comparison' | 'settings'

  // UI state
  const [selectedStopIds, setSelectedStopIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState({ progress: 0, stage: "" });
  const [autoClusterProgress, setAutoClusterProgress] = useState({ progress: 0, stage: "" });
  const [autoClusterLoading, setAutoClusterLoading] = useState(false);
  const [clusterMode, setClusterMode] = useState("last_saved"); // 'last_saved' or 'fresh_run'
  const [clusterCacheExists, setClusterCacheExists] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showVehicleChains, setShowVehicleChains] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showOfficeDialog, setShowOfficeDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [demandAdjustment, setDemandAdjustment] = useState(0); // -100 to +100
  const [officeForm, setOfficeForm] = useState({ name: "Office", latitude: "", longitude: "" });
  const [selectedRouteEntity, setSelectedRouteEntity] = useState(null); // Entity whose routes to show on map
  const [showRoutesPanel, setShowRoutesPanel] = useState(false);
  const [routeGeometries, setRouteGeometries] = useState({}); // Cache for route geometries
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapShift, setHeatmapShift] = useState("peak"); // 'peak', 'A', 'B', 'C'
  const [routeMode, setRouteMode] = useState("dynamic"); // 'static' or 'dynamic'
  const [fleetMix, setFleetMix] = useState("constrained"); // 'constrained' or 'ideal'
  const [selectedZoneIds, setSelectedZoneIds] = useState(new Set()); // filter map to zones
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [shifts, setShifts] = useState([]); // Shift timings
  const [showAssignBuses, setShowAssignBuses] = useState(false);
  const [individualVehicles, setIndividualVehicles] = useState([]);
  const [busAssignments, setBusAssignments] = useState([]);
  const [hasOptimized, setHasOptimized] = useState(false);

  // Partial-cluster diagnostics from the last Auto-Cluster run.
  // Holds `{assigned_count, unassigned_count, unassigned: [{stop_id, stop_name, nearest_distance_km}]}`.
  // The banner remains until the user resolves — either by manually
  // assigning the unassigned stops or by re-running with Fresh Run.
  const [partialClusterInfo, setPartialClusterInfo] = useState(null);
  // Confirmation modal state for destructive Fresh Run switch.
  const [pendingClusterModeChange, setPendingClusterModeChange] = useState(null);
  // Warning modal state when > 10% of demand-stops are newly-added.
  const [manyNewStopsWarning, setManyNewStopsWarning] = useState(null); // { new_count, demand_count, ratio }

  // Single route map view state
  const [focusedRoute, setFocusedRoute] = useState(null); // { route, baseRoute, entity, geometry, allRoutes, currentIndex, shiftIdle }
  const [focusedRouteLoading, setFocusedRouteLoading] = useState(false);
  const [focusedShift, setFocusedShift] = useState("peak"); // Shift shown in the focused-route view

  // Roster cleaning state
  const [rosterStatus, setRosterStatus] = useState({ uploaded: false, count: 0, cleaned: false });
  const [cleaningSummary, setCleaningSummary] = useState(null);
  const [cleaningLoading, setCleaningLoading] = useState(false);
  const [cleaningDone, setCleaningDone] = useState(false);
  // Preview-before-commit dialog for Process & Validate.
  // Holds the dry-run response from /process-data/preview while the user
  // confirms or cancels.
  const [pvPreview, setPvPreview] = useState(null);
  const [pvPreviewLoading, setPvPreviewLoading] = useState(false);
  const [uploadedFormat, setUploadedFormat] = useState(null); // 'roster' or 'demand'
  const [rosterStartDate, setRosterStartDate] = useState("");
  const [rosterEndDate, setRosterEndDate] = useState("");

  // Global task lock state — single in-flight pipeline task awareness.
  // When set, mutating buttons (uploads, Process & Validate, Auto-Cluster,
  // Optimize) are disabled and the task panel shows current progress.
  const [activeTask, setActiveTask] = useState(null);
  const isTaskRunning = !!activeTask;

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [stopsRes, groupsRes, vehiclesRes, officeRes, shiftsRes, rosterRes, rosterConfigRes] = await Promise.all([
        axios.get(`${API}/stops`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/office`),
        axios.get(`${API}/shifts`),
        axios.get(`${API}/roster-status`),
        axios.get(`${API}/roster-config`),
      ]);
      setStops(stopsRes.data);
      setGroups(groupsRes.data);
      // Auto-select all zones + ungrouped on load
      if (groupsRes.data.length > 0) {
        setSelectedZoneIds(prev => prev.size === 0 ? new Set([...groupsRes.data.map(g => g.id), "__ungrouped__", "__excluded__"]) : prev);
      }
      setVehicles(vehiclesRes.data);
      setOfficeStop(officeRes.data);
      setShifts(shiftsRes.data || []);
      setRosterStatus(rosterRes.data || { uploaded: false, count: 0, cleaned: false });
      // Auto-populate cleaning stats from roster-status response
      if (rosterRes.data?.cleaned && rosterRes.data?.total_rosters != null) {
        setCleaningSummary({
          total_rosters: rosterRes.data.total_rosters,
          high_confidence_count: rosterRes.data.high_confidence_count || 0,
          low_confidence_count: rosterRes.data.low_confidence_count || 0,
          unmapped_count: rosterRes.data.unmapped_count || 0,
          duplicates_dropped_count: rosterRes.data.duplicates_dropped_count || 0,
        });
        setCleaningDone(true);
      }
      if (rosterConfigRes.data) {
        setRosterStartDate(rosterConfigRes.data.start_date || "");
        setRosterEndDate(rosterConfigRes.data.end_date || "");
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // On page unload (close/reload): cancel running optimization
    const handleUnload = () => {
      navigator.sendBeacon(`${API}/optimize/cancel`);
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [fetchData]);

  // Rehydrate optimize results on mount / after refresh. The backend keeps
  // the last completed result in optimization_snapshots and exposes it via
  // /optimize/status (Postgres-backed, multi-worker safe). Without this,
  // refreshing during/after a Generate Routes run loses the UI even though
  // the backend has the routes ready (Jul 2026 fix).
  useEffect(() => {
    if (!buid) return;
    let cancelled = false;
    (async () => {
      try {
        console.log('[rehydrate] fetching /optimize/status for buid=', buid);
        const res = await axios.get(`${API}/optimize/status`);
        const task = res.data;
        console.log('[rehydrate] status=', task.status, 'has_result=', task.has_result);
        if (cancelled) return;
        if (task.status === "completed" && task.has_result) {
          // Heavy payload lives at /optimize/result — one call, only when we
          // know a completed result actually exists (Jul 2026 split).
          const resultRes = await axios.get(`${API}/optimize/result`);
          if (cancelled) return;
          const data = { ...resultRes.data.result };
          if (data.results) {
            data.results = data.results.filter(r => (r.peak_demand || 0) > 0);
            data.results.sort(zoneEntitySortComparator);
          }
          console.log('[rehydrate] hydrating UI with', data.results?.length || 0, 'zones');
          setOptimizationResults(data);
          setShowResults(true);
          setHasOptimized(true);
          setOptimizationProgress({ progress: 100, stage: "Complete" });
        }
      } catch (e) {
        console.warn('[rehydrate] failed (non-fatal):', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buid]);

  // Re-fetch once the user submits the BUID modal — the initial fetchData
  // fired without an X-BUID header (fresh tab), so it returned empty data.
  useEffect(() => {
    if (buid) fetchData();
  }, [buid, fetchData]);

  // Poll /tasks/active continuously so buttons stay disabled while ANY
  // pipeline task (uploads, Process & Validate, Auto-Cluster, Optimize)
  // is running — regardless of which tab/user started it.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/tasks/active`);
        if (cancelled) return;
        const newTask = res.data?.active || null;
        // When a task finishes, refresh the main data view
        setActiveTask((prev) => {
          if (prev && !newTask) {
            fetchData();
          }
          return newTask;
        });
      } catch { /* ignore transient errors */ }
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [fetchData]);

  // Toggle stop selection
  const toggleStopSelection = (stopId) => {
    setSelectedStopIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stopId)) {
        newSet.delete(stopId);
      } else {
        newSet.add(stopId);
      }
      return newSet;
    });
  };

  // Create group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (selectedStopIds.size === 0) {
      toast.error("Please select at least one stop");
      return;
    }

    try {
      await axios.post(`${API}/groups`, {
        name: newGroupName,
        stop_ids: Array.from(selectedStopIds),
      });
      toast.success(`Group "${newGroupName}" created`);
      setShowGroupDialog(false);
      setNewGroupName("");
      setSelectedStopIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    }
  };

  // Rename group
  const handleRenameGroup = async (groupId) => {
    if (!editGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    try {
      await axios.patch(`${API}/groups/${groupId}/rename`, {
        name: editGroupName,
      });
      toast.success("Group renamed");
      setEditingGroupId(null);
      setEditGroupName("");
      fetchData();
    } catch (error) {
      toast.error("Failed to rename group");
    }
  };

  // Delete group
  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/groups/${groupId}`);
      toast.success("Group deleted");
      fetchData();
      setHasOptimized(false);
      setOptimizationResults(null);
      setBusAssignments([]);
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  // Set office stop
  const handleSetOffice = async () => {
    if (!officeForm.latitude || !officeForm.longitude) {
      toast.error("Please enter latitude and longitude");
      return;
    }
    try {
      await axios.post(`${API}/office`, {
        name: officeForm.name || "Office",
        latitude: parseFloat(officeForm.latitude),
        longitude: parseFloat(officeForm.longitude),
      });
      toast.success("Office location set");
      setShowOfficeDialog(false);
      setOfficeForm({ name: "Office", latitude: "", longitude: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to set office location");
    }
  };

  // Delete office stop
  const handleDeleteOffice = async () => {
    try {
      await axios.delete(`${API}/office`);
      toast.success("Office location removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove office");
    }
  };

  // Save roster date range
  const handleSaveRosterDates = async (start, end) => {
    if (!start || !end) return;
    try {
      await axios.post(`${API}/roster-config`, { start_date: start, end_date: end });
    } catch (e) {
      console.error("Failed to save roster config:", e);
    }
  };

  // Clean nodal points - map roster to stops master
  const handleCleanNodalPoints = async () => {
    if (!rosterStatus.uploaded) {
      toast.error("Please upload the Rosters file first");
      return;
    }
    if (stops.length === 0) {
      toast.error("Please upload the Stops Master first");
      return;
    }

    setCleaningLoading(true);
    try {
      const response = await axios.post(`${API}/clean-nodal-points`);
      setCleaningSummary(response.data);
      setCleaningDone(true);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cleaning failed");
    } finally {
      setCleaningLoading(false);
    }
  };

  // Download cleaned roster
  const [cleanedRosterDownloading, setCleanedRosterDownloading] = useState(false);
  const [duplicatesDownloading, setDuplicatesDownloading] = useState(false);
  const [tripsheetDownloading, setTripsheetDownloading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const handleDownloadCleanedRoster = async () => {
    setCleanedRosterDownloading(true);
    try {
      const res = await axios.get(`${API}/download/roster-cleaned`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "roster_cleaned.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download cleaned roster");
    } finally {
      setCleanedRosterDownloading(false);
    }
  };

  // Download the duplicates dropped during the most recent Process & Validate run.
  const handleDownloadRosterDuplicates = async () => {
    setDuplicatesDownloading(true);
    try {
      const res = await axios.get(`${API}/download/roster-duplicates`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "roster_duplicates.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to download duplicates");
    } finally {
      setDuplicatesDownloading(false);
    }
  };

  // Poll the backend auto-cluster task until it finishes. Used by both the
  // user-triggered run and the on-mount resume flow so a page refresh doesn't
  // lose visibility into an in-progress background cluster.
  const pollAutoClusterStatus = async () => {
    let attempts = 0;
    const maxAttempts = 1200; // ~60 minutes (3s intervals)
    while (attempts < maxAttempts) {
      await new Promise(r => setTimeout(r, 3000));
      attempts++;
      try {
        const statusRes = await axios.get(`${API}/auto-cluster/status`);
        const task = statusRes.data;
        if (typeof task.cache_exists === "boolean") setClusterCacheExists(task.cache_exists);
        setAutoClusterProgress(prev => {
          const newProgress = task.progress || 0;
          if (newProgress < prev.progress && task.status === "processing") return prev;
          return { progress: newProgress, stage: task.stage || "Processing..." };
        });

        if (task.status === "completed" && task.result) {
          const data = task.result;
          toast.success(data.message);
          await fetchData();
          // On cross-worker completion the server can't ship the cluster list
          // (it lives only in the worker that ran the task), so it sets
          // `cross_worker: true` and we rebuild the selection from the freshly
          // fetched /api/groups list (Jul 2026 multi-worker fix).
          let newGroups = data.clusters || [];
          if (data.cross_worker || newGroups.length === 0) {
            try {
              const gr = await axios.get(`${API}/groups`);
              newGroups = gr.data || [];
            } catch (_e) { /* fetchData above should already have populated groups */ }
          }
          setSelectedZoneIds(new Set([...newGroups.map(g => g.id), "__ungrouped__", "__excluded__"]));
          setHasOptimized(false);
          setOptimizationResults(null);
          setBusAssignments([]);
          setAutoClusterProgress({ progress: 100, stage: "Complete" });
          setAutoClusterLoading(false);
          return;
        } else if (task.status === "failed") {
          toast.error(task.error || "Auto-clustering failed");
          setAutoClusterLoading(false);
          return;
        } else if (task.status === "cancelled") {
          toast.info("Auto-clustering was cancelled");
          setAutoClusterLoading(false);
          return;
        } else if (task.status === "idle") {
          // Backend has no active task — stop polling silently (resume flow only)
          setAutoClusterLoading(false);
          return;
        }
      } catch (pollErr) {
        console.warn("Auto-cluster poll error:", pollErr);
      }
    }
    // Polling window exhausted — one final check in case backend finished during last interval
    try {
      const finalRes = await axios.get(`${API}/auto-cluster/status`);
      const finalTask = finalRes.data;
      if (finalTask.status === "completed" && finalTask.result) {
        const data = finalTask.result;
        toast.success(data.message);
        await fetchData();
        const newGroups = data.clusters || [];
        setSelectedZoneIds(new Set([...newGroups.map(g => g.id), "__ungrouped__", "__excluded__"]));
        setHasOptimized(false);
        setOptimizationResults(null);
        setBusAssignments([]);
        setAutoClusterProgress({ progress: 100, stage: "Complete" });
        setAutoClusterLoading(false);
        return;
      }
    } catch {
      // ignore — fall through to timeout message
    }
    toast.error("Auto-clustering is taking longer than 60 minutes. It may still be running in the background — refresh the page in a few minutes to see if clusters were created.");
    setAutoClusterLoading(false);
  };

  // Auto-cluster stops (async with polling)
  const handleAutoCluster = async () => {
    if (!officeStop) {
      toast.error("Please set office location first");
      return;
    }
    if (vehicles.length === 0) {
      toast.error("Please upload vehicle data first");
      return;
    }

    setAutoClusterLoading(true);
    setAutoClusterProgress({ progress: 0, stage: "Starting auto-cluster..." });
    try {
      await axios.post(`${API}/auto-cluster/start`, { mode: clusterMode });
      await pollAutoClusterStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start auto-clustering");
      setAutoClusterLoading(false);
    }
  };

  // Resume polling on mount / on BUID change if a cluster is already running
  // in the background (e.g., user hit refresh during a long first-time run).
  // Also seeds clusterCacheExists so the toggle visibility reflects current
  // backend state.  Depends on `buid` because the fetch relies on X-BUID
  // header — an empty buid returns cache_exists:false for the default tenant,
  // so we must re-fetch once the user selects their BUID via the modal.
  useEffect(() => {
    if (!buid) return;  // wait until user has entered a BUID
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auto-cluster/status`);
        const task = res.data;
        if (cancelled) return;
        if (typeof task.cache_exists === "boolean") {
          setClusterCacheExists(task.cache_exists);
          // Sync toggle default with reality: no cache → Fresh Run is the
          // only usable option; cache exists → Last Saved is preferred.
          setClusterMode(task.cache_exists ? "last_saved" : "fresh_run");
        }
        if (task && task.status === "processing") {
          setAutoClusterLoading(true);
          setAutoClusterProgress({
            progress: task.progress || 0,
            stage: task.stage || "Resuming background auto-cluster...",
          });
          toast.info("Auto-clustering still running in the background — resuming progress");
          pollAutoClusterStatus();
        }
      } catch {
        // silent — no active run
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buid]);

  // Handle cluster-mode toggle click.  Guards the destructive Fresh Run
  // switch behind a confirmation modal — but only when clusters actually
  // exist (nothing to protect on a fresh app).  Also silently invalidates
  // hasOptimized so the "See Routes" button reverts to "Generate Routes".
  const handleClusterModeChange = (newMode) => {
    if (newMode === clusterMode) return;
    if (newMode === "fresh_run" && clusterCacheExists && groups.length > 0) {
      setPendingClusterModeChange(newMode);
      return;
    }
    setClusterMode(newMode);
    setHasOptimized(false);
  };

  // Count stops that have demand but no group assignment — these are
  // "new since last cluster" from the user's perspective.  Used both for
  // the >10% warning modal and the persistent banner after partial-cluster.
  const computeNewStopsInfo = () => {
    const withDemand = stops.filter((s) => {
      const sd = s.shift_demands || {};
      return Object.values(sd).some((v) => v > 0);
    });
    const newStops = withDemand.filter((s) => !s.group_id);
    return {
      demand_count: withDemand.length,
      new_count: newStops.length,
      ratio: withDemand.length > 0 ? newStops.length / withDemand.length : 0,
    };
  };

  // Cancel auto-cluster
  const handleCancelAutoCluster = async () => {
    try {
      await axios.post(`${API}/auto-cluster/cancel`);
      toast.info("Auto-clustering cancelled");
    } catch {
      // ignore
    }
  };

  // Internal: run auto-cluster + wait for it, updating optimizationProgress
  // to span 0-40% (leaving 40-100% for the optimize phase).  Returns the
  // task result on success, or throws on failure/cancel.
  //
  // Feb 2026 production 504 fix: tolerate transient 5xx / network errors
  // during status polling. A single flaky poll used to abort the whole
  // clustering flow even though the background task was still healthy.
  const runAutoClusterAndWait = async (mode) => {
    await axios.post(`${API}/auto-cluster/start`, { mode });
    const maxAttempts = 800; // ~40 minutes at 3s intervals
    let consecutivePollErrors = 0;
    let consecutiveIdle = 0;
    const MAX_CONSECUTIVE_POLL_ERRORS = 10; // ~30s of continuous failures
    const MAX_CONSECUTIVE_IDLE = 20; // ~60s of "no task" — generous enough
                                     // for cross-worker DB propagation on
                                     // multi-worker uvicorn (Jul 2026)
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      let task;
      try {
        const res = await axios.get(`${API}/auto-cluster/status`);
        task = res.data;
        consecutivePollErrors = 0;
      } catch (pollErr) {
        consecutivePollErrors++;
        console.warn(`Auto-cluster status poll error (${consecutivePollErrors}/${MAX_CONSECUTIVE_POLL_ERRORS}):`, pollErr?.message || pollErr);
        if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
          throw new Error("Lost connection to the server while clustering. The task may still be running — refresh the page in a minute to check.");
        }
        continue; // transient — keep polling
      }
      const raw = task.progress || 0;
      // Compress cluster progress into 0-40% band
      setOptimizationProgress({
        progress: Math.round(raw * 0.4),
        stage: task.stage ? `Clustering: ${task.stage}` : "Clustering...",
      });
      if (task.status === "completed" && task.result) return task.result;
      if (task.status === "failed") throw new Error(task.error || "Clustering failed");
      if (task.status === "cancelled") throw new Error("Clustering cancelled");
      if (task.status === "idle") {
        // Backend has no clustering task — pod restarted or task was cleared.
        // Bail rather than spin forever (Jul 2026 stuck-UI fix).
        consecutiveIdle++;
        if (consecutiveIdle >= MAX_CONSECUTIVE_IDLE) {
          throw new Error("Clustering task lost — the server may have restarted mid-run. Please click Generate Routes again.");
        }
      } else {
        consecutiveIdle = 0;
      }
    }
    throw new Error("Clustering timed out");
  };

  // Run optimization — orchestrates cluster (if needed) → optimize.
  const handleOptimize = async (opts = {}) => {
    if (vehicles.length === 0) {
      toast.error("Please upload vehicle data first");
      return;
    }
    if (stops.length === 0) {
      toast.error("Please upload stops first");
      return;
    }
    if (!officeStop) {
      toast.error("Please set office location first");
      return;
    }

    // If already optimized and no cluster changes, just show results
    if (hasOptimized && optimizationResults) {
      setShowResults(true);
      return;
    }

    // ── Pre-cluster: gate destructive scenarios ────────────────────────────
    // If Last Saved is picked but > 10% of stops-with-demand are newly-added
    // (i.e. have no group_id), warn upfront — a bulk partial-cluster to
    // nearest zone can distort per-zone capacity.  Skipped when the caller
    // already acknowledged via `opts.acknowledgedManyNewStops`.
    if (clusterMode === "last_saved" && clusterCacheExists && !opts.acknowledgedManyNewStops) {
      const info = computeNewStopsInfo();
      if (info.new_count > 0 && info.ratio > 0.1) {
        setManyNewStopsWarning(info);
        return;
      }
    }

    setLoading(true);
    setOptimizationProgress({ progress: 0, stage: "Starting..." });
    setRouteGeometries({});
    setBusAssignments([]);
    try {
      // ── Phase 1: cluster (if needed) ───────────────────────────────────
      // Fresh Run always clusters.  Last Saved clusters too — the backend
      // no-ops on the cache-hit path (fast) or partial-clusters new stops.
      // When no cache exists, Last Saved gracefully falls through to a
      // fresh cluster (backend handles).
      try {
        const clusterResult = await runAutoClusterAndWait(clusterMode);
        setClusterCacheExists(true);
        // After a successful Fresh Run, switch the default back to Last
        // Saved so subsequent runs preserve manual edits (per user spec).
        if (clusterMode === "fresh_run") {
          setClusterMode("last_saved");
        }
        // Surface partial-cluster diagnostics as a persistent banner if
        // any new stops were auto-assigned or left ungrouped.
        const pc = clusterResult?.partial_cluster;
        if (pc && (pc.assigned_count > 0 || pc.unassigned_count > 0)) {
          setPartialClusterInfo(pc);
        } else {
          setPartialClusterInfo(null);
        }
        // Refresh groups/stops after clustering
        await fetchData();
      } catch (clusterErr) {
        toast.error(`Clustering failed: ${clusterErr.message}`);
        setLoading(false);
        return;
      }

      // ── Phase 2: optimize ──────────────────────────────────────────────
      setOptimizationProgress({ progress: 40, stage: "Starting route generation..." });
      // Retry POST /optimize on transient 5xx (Cloudflare 520 / origin restart)
      // so a single edge blip after Auto-Cluster doesn't kill the pipeline.
      // If POST accepts once (task started) OR returns 409 (another task
      // already running — same tenant retriggered), we proceed to polling.
      const POST_MAX_ATTEMPTS = 4;
      let postOk = false;
      let lastPostErr = null;
      for (let attempt = 1; attempt <= POST_MAX_ATTEMPTS; attempt++) {
        try {
          await axios.post(`${API}/optimize`, {
            demand_adjustment: demandAdjustment,
            shift: "peak",
            route_mode: routeMode,
            fleet_mix: fleetMix,
          });
          postOk = true;
          break;
        } catch (postErr) {
          const status = postErr?.response?.status;
          lastPostErr = postErr;
          // 409 = task already running (server accepted a prior POST that we
          // didn't see the 200 for — likely edge hiccup that returned 5xx
          // to the client but reached the origin). Treat as success and
          // start polling.
          if (status === 409) {
            postOk = true;
            break;
          }
          // Retry on 5xx / network errors. Bail immediately on 4xx.
          if (status && status < 500) throw postErr;
          if (attempt < POST_MAX_ATTEMPTS) {
            setOptimizationProgress({
              progress: 40,
              stage: `Retrying route generation start (${attempt}/${POST_MAX_ATTEMPTS - 1})...`,
            });
            // exponential-ish backoff: 2s, 4s, 6s
            await new Promise(r => setTimeout(r, 2000 * attempt));
          }
        }
      }
      if (!postOk) throw lastPostErr || new Error("Failed to start optimization");

      // Poll for results
      let attempts = 0;
      let consecutiveIdle = 0;
      const maxAttempts = 400; // ~20 minutes (3s intervals)
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/optimize/status`);
          const task = statusRes.data;
          // Compress optimization progress into 40-100% band (0-40 was cluster)
          const optRaw = task.progress || 0;
          setOptimizationProgress({
            progress: 40 + Math.round(optRaw * 0.6),
            stage: task.stage || "Generating routes...",
          });
          if (task.status === "completed" && task.has_result) {
            // Fetch the heavy result payload only once, after status flips
            // to completed (Jul 2026 status/result split — poll stays tiny,
            // this call fires exactly once per run).
            const resultRes = await axios.get(`${API}/optimize/result`);
            const data = resultRes.data.result;
            if (data.results) {
              data.results = data.results.filter(r => {
                const peakDemand = r.peak_demand || 0;
                return peakDemand > 0;
              });
              // Order zones ascending by name ("Zone 1" → "Zone N"; custom
              // names last alphabetically) so See-Routes, Assign-Buses and
              // Excel-export share the sidebar's order.  Backend also
              // sorts the same way — this is a defensive layer for cached
              // snapshots produced before the backend fix shipped.
              data.results.sort(zoneEntitySortComparator);
            }
            setOptimizationResults(data);
            setShowResults(true);
            setHasOptimized(true);
            setOptimizationProgress({ progress: 100, stage: "Complete" });
            // Refresh sidebar groups from DB (may have been updated by OSRM cluster splits)
            const refreshRes = await axios.get(`${API}/groups`);
            setGroups(refreshRes.data);
            // Reset zone filter to include all groups (including newly split ones)
            setSelectedZoneIds(new Set([...refreshRes.data.map(g => g.id), "__ungrouped__", "__excluded__"]));
            // Also refresh stops (their group_id may have changed)
            const refreshStops = await axios.get(`${API}/stops`);
            setStops(refreshStops.data);
            toast.success(`Optimization complete (${routeMode === "static" ? "Static" : "Dynamic"} / ${fleetMix === "ideal" ? "Ideal Mix" : "Constrained"})`);
            setLoading(false);
            return;
          } else if (task.status === "failed") {
            toast.error(task.error || "Optimization failed");
            setLoading(false);
            return;
          } else if (task.status === "cancelled") {
            toast.error("Optimization was cancelled");
            setLoading(false);
            return;
          } else if (task.status === "idle") {
            // Backend has no active optimize task. Either the pod restarted
            // (in-memory state wiped) or the task was cleared elsewhere.
            // With multi-worker uvicorn, cross-worker DB reads can lag
            // briefly — hence a generous 20-poll (~60s) tolerance before
            // we give up (Jul 2026 multi-worker fix).
            consecutiveIdle++;
            if (consecutiveIdle >= 20) {
              toast.error("Optimization task lost — the server may have restarted mid-run. Please click Generate Routes again.");
              setLoading(false);
              return;
            }
          } else {
            consecutiveIdle = 0;  // any non-idle status resets the counter
          }
          // Still processing, continue polling
        } catch (pollErr) {
          // Network error on poll, continue trying
          console.warn("Poll error:", pollErr);
        }
      }
      toast.error("Optimization timed out after 20 minutes. The backend may still be running — try refreshing.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start optimization");
    } finally {
      setLoading(false);
    }
  };

  // Export routes to Excel
  const handleExportRoutes = async () => {
    if (!optimizationResults) {
      toast.error("No optimization results to export");
      return;
    }
    setExportLoading(true);
    try {
      // Get office name
      const officeName = officeStop?.name || "Office";
      
      // Lightweight payload — optimization data is read from server state.
      // We send `vehicle_id` (e.g. B2-V1) so the backend can map vendor +
      // bus_no to each physical vehicle, not just the logical route bus_id.
      const lightPayload = {
        office_name: officeName,
        bus_assignments: (busAssignments || []).filter(a => a.vehicle_no || a.vendor).map(a => ({
          vehicle_id: a.vehicle_id,
          bus_id: a.bus_id,
          vendor: a.vendor,
          vehicle_no: a.vehicle_no,
        })),
      };

      // Download Excel file — backend reads full results from state
      const buidHeader = getBUID() ? { "X-BUID": getBUID() } : {};
      const excelRes = await fetch(`${API}/export/routes-from-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...buidHeader },
        body: JSON.stringify(lightPayload),
      });
      if (!excelRes.ok) {
        const errText = await excelRes.text().catch(() => "");
        throw new Error(`Export failed (${excelRes.status}): ${errText.slice(0, 200)}`);
      }
      const excelBlob = await excelRes.blob();
      const excelUrl = URL.createObjectURL(excelBlob);
      const excelLink = document.createElement("a");
      excelLink.href = excelUrl;
      excelLink.download = "bus_routes_export.xlsx";
      document.body.appendChild(excelLink);
      excelLink.click();
      document.body.removeChild(excelLink);

      // Download JSON file with slight delay to avoid browser blocking
      setTimeout(async () => {
        try {
          const jsonRes = await fetch(`${API}/export/routes-json-from-state`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...buidHeader },
            body: JSON.stringify(lightPayload),
          });
          if (!jsonRes.ok) return;
          const jsonData = await jsonRes.json();
          const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: "application/json" });
          const jsonUrl = URL.createObjectURL(jsonBlob);
          const jsonLink = document.createElement("a");
          jsonLink.href = jsonUrl;
          jsonLink.download = "bus_routes_export.json";
          document.body.appendChild(jsonLink);
          jsonLink.click();
          document.body.removeChild(jsonLink);
          URL.revokeObjectURL(jsonUrl);
        } catch (err) {
          console.error("JSON export error:", err);
        }
      }, 1000);

      URL.revokeObjectURL(excelUrl);
      toast.success("Routes exported (Excel + JSON)");
    } catch (error) {
      toast.error("Failed to export routes: " + (error.message || "Unknown error"));
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  // Download bus-wise tripsheet ZIP (per-vehicle PDFs grouped by vendor)
  const handleDownloadTripsheet = async () => {
    if (!optimizationResults) {
      toast.error("No optimization results to export");
      return;
    }
    setTripsheetDownloading(true);
    try {
      const officeName = officeStop?.name || "Office";
      const buidHeader = getBUID() ? { "X-BUID": getBUID() } : {};
      const res = await fetch(`${API}/export/tripsheet-from-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...buidHeader },
        body: JSON.stringify({
          office_name: officeName,
          bus_assignments: (busAssignments || []).filter(a => a.vehicle_no || a.vendor).map(a => ({
            vehicle_id: a.vehicle_id,
            bus_id: a.bus_id,
            vendor: a.vendor,
            vehicle_no: a.vehicle_no,
          })),
        }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(`Tripsheet failed (${res.status}): ${errText.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Tripsheet.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Tripsheet downloaded");
    } catch (error) {
      toast.error("Failed to download tripsheet: " + (error.message || "Unknown error"));
      console.error("Tripsheet error:", error);
    } finally {
      setTripsheetDownloading(false);
    }
  };

  // Update stop's group assignment
  const handleUpdateStopGroup = async (stopId, newGroupId) => {
    try {
      await axios.put(`${API}/stops/${stopId}/group`, {
        group_id: newGroupId || null,
      });
      toast.success("Stop group updated");
      fetchData();
      setHasOptimized(false);
      setOptimizationResults(null);
      setBusAssignments([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update stop group");
    }
  };

  // Persist the multi-select set of "forced anchor zones" for a stop.
  // On next Generate Routes, the backend anchor pass injects this stop
  // into each of those zones' bus routes, bypassing the corridor/demand
  // gates.  The home zone (`group_id`) is separate and unchanged.
  const handleUpdateStopAnchorZones = async (stopId, zoneIds) => {
    try {
      await axios.put(`${API}/stops/${stopId}/anchor-zones`, {
        forced_anchor_zone_ids: zoneIds,
      });
      toast.success(zoneIds.length ? `Anchored into ${zoneIds.length} zone(s)` : "Cleared forced anchors");
      // Refresh stops so the UI's `forced_anchor_zone_ids` matches DB.
      const refresh = await axios.get(`${API}/stops`);
      setStops(refresh.data);
      // Mutating anchor set invalidates the last optimization result.
      setHasOptimized(false);
      setOptimizationResults(null);
      setBusAssignments([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update anchor zones");
    }
  };

  // Fetch actual road route from OSRM via backend
  const fetchRouteGeometry = async (routeKey, coordinates) => {
    // Check cache first
    if (routeGeometries[routeKey]) {
      return routeGeometries[routeKey];
    }

    try {
      const response = await axios.post(`${API}/route`, {
        coordinates: coordinates, // [[lng, lat], ...]
      });
      
      // Cache the result
      setRouteGeometries(prev => ({
        ...prev,
        [routeKey]: response.data
      }));
      
      return response.data;
    } catch (error) {
      console.error("Failed to fetch route:", error);
      return null;
    }
  };

  // Fetch all routes for selected entity
  const fetchAllRoutes = async (entity, routes) => {
    if (!routes?.length || !officeStop) return;

    setLoadingRoutes(true);

    for (const route of routes) {
      const routeKey = `${entity.entity_id}-${route.bus_id}`;
      
      // Build coordinates: office -> stops -> office
      const coordinates = [
        [officeStop.longitude, officeStop.latitude], // OSRM uses [lng, lat]
      ];
      
      route.stops.forEach(stop => {
        coordinates.push([stop.longitude, stop.latitude]);
      });
      
      // Return to office
      coordinates.push([officeStop.longitude, officeStop.latitude]);
      
      await fetchRouteGeometry(routeKey, coordinates);
    }
    
    setLoadingRoutes(false);
  };

  // Routes to draw on the map for the selected entity (peak snapshot,
  // skipping ghost entries with no stops).
  const mapDisplayedRoutes = useMemo(() => {
    if (!selectedRouteEntity) return [];
    return (selectedRouteEntity.bus_routes || []).filter(r => r.stops?.length > 0);
  }, [selectedRouteEntity]);

  // Fetch routes when entity is selected
  useEffect(() => {
    if (selectedRouteEntity && officeStop) {
      fetchAllRoutes(selectedRouteEntity, mapDisplayedRoutes);
    }
  }, [selectedRouteEntity, officeStop]);

  // Resolve a bus's route for a given shift ("peak" = top-level snapshot)
  const resolveRouteForShift = (entity, busId, shiftKey) => {
    if (shiftKey === "peak") {
      return entity.bus_routes?.find(br => br.bus_id === busId) || null;
    }
    return entity.shift_deployments?.[shiftKey]?.bus_routes?.find(br => br.bus_id === busId) || null;
  };

  // View a single route on the map (optionally for a specific shift)
  const viewRouteOnMap = async (route, entity, shiftKey = "peak") => {
    if (!officeStop) return;
    setFocusedShift(shiftKey);
    setFocusedRouteLoading(true);
    setShowResults(false);
    setSelectedRouteEntity(null); // Clear zone-level polylines

    // Build all routes list for prev/next navigation.
    // IMPORTANT: bus_id is NOT unique across zones — Zone 1 and Zone 2 can
    // both have a bus named "B1". Every lookup below MUST match on the
    // composite key (entity_id, bus_id), never on bus_id alone.
    //
    // We also drop empty (0-stop) routes.  These appear in shift_deployments
    // as "ghost" entries when the anchor-stops pass replaces top-level
    // bus_routes with a smaller fleet post-commit — the old bus_ids remain
    // in shift_deployments but own no stops.  Filtering them here keeps the
    // nav consistent with what the user sees.  (Backend TODO: regenerate
    // shift_deployments after anchor commit so the two views agree.)
    const hasStops = (br) => Array.isArray(br?.stops) && br.stops.length > 0;
    const allRoutes = [];
    if (optimizationResults) {
      for (const r of optimizationResults.results) {
        const color = groups.find(g => g.id === r.entity_id)?.color || "#3B82F6";
        for (const br of r.bus_routes || []) {
          if (!hasStops(br)) continue;
          allRoutes.push({ route: br, entity: r, color });
        }
        for (const [, sd] of Object.entries(r.shift_deployments || {})) {
          for (const br of sd.bus_routes || []) {
            if (!hasStops(br)) continue;
            if (!allRoutes.find(a =>
              a.route.bus_id === br.bus_id && a.entity.entity_id === r.entity_id
            )) {
              allRoutes.push({ route: br, entity: r, color });
            }
          }
        }
      }
    }
    const currentIndex = allRoutes.findIndex(a =>
      a.route.bus_id === route.bus_id && a.entity.entity_id === entity.entity_id
    );
    const entityColor = groups.find(g => g.id === entity.entity_id)?.color || "#3B82F6";

    // Resolve the shift-specific version of this bus's route.
    const displayRoute = shiftKey === "peak" ? route : resolveRouteForShift(entity, route.bus_id, shiftKey);
    const hasTrip = displayRoute && displayRoute.stops?.length > 0 &&
      (displayRoute.total_passengers || 0) > 0 && displayRoute.status !== "Idle";
    if (!hasTrip) {
      setFocusedRoute({
        route: displayRoute || route, baseRoute: route, entity, geometry: null,
        shiftIdle: true, allRoutes, currentIndex, color: entityColor
      });
      setFocusedRouteLoading(false);
      return;
    }

    // Fetch OSRM geometry: stops in order → office
    const coordinates = displayRoute.stops.map(s => [s.longitude, s.latitude]);
    coordinates.push([officeStop.longitude, officeStop.latitude]);

    try {
      const res = await axios.post(`${API}/route`, { coordinates });
      setFocusedRoute({
        route: displayRoute, baseRoute: route, entity, geometry: res.data, allRoutes, currentIndex, color: entityColor
      });
    } catch {
      // Fallback to straight line
      setFocusedRoute({
        route: displayRoute, baseRoute: route, entity,
        geometry: { type: "LineString", coordinates, is_fallback: true },
        allRoutes, currentIndex, color: entityColor
      });
    }
    setFocusedRouteLoading(false);
  };

  // Navigate to prev/next route (keeps the selected shift)
  const navigateRoute = async (direction) => {
    if (!focusedRoute) return;
    const { allRoutes, currentIndex } = focusedRoute;
    const newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= allRoutes.length) return;
    const { route, entity } = allRoutes[newIndex];
    await viewRouteOnMap(route, entity, focusedShift);
  };

  // Switch the shift shown in the focused-route view
  const switchFocusedShift = async (shiftKey) => {
    if (!focusedRoute || shiftKey === focusedShift) return;
    await viewRouteOnMap(focusedRoute.baseRoute || focusedRoute.route, focusedRoute.entity, shiftKey);
  };



  // ── Anchor stop lookup ─────────────────────────────────────────────
  // Merges two sources of "this stop is anchored to zones [z1, z2, ...]":
  //   (a) `optimizationResults.summary.anchor_stops.stop_anchor_zones`  ← last algorithm run's decisions
  //   (b) `stop.forced_anchor_zone_ids`                                 ← user-forced overrides (persist across runs)
  // Returns a `stopId -> Set(zoneId)` map. A stop is considered "an anchor" iff its set is non-empty.
  const anchorZonesByStopId = useMemo(() => {
    const m = new Map();
    const fromRun = optimizationResults?.summary?.anchor_stops?.stop_anchor_zones || {};
    Object.entries(fromRun).forEach(([sid, zones]) => {
      m.set(sid, new Set(zones || []));
    });
    stops.forEach((s) => {
      const forced = s.forced_anchor_zone_ids || [];
      if (!forced.length) return;
      const cur = m.get(s.id) || new Set();
      forced.forEach((z) => cur.add(z));
      m.set(s.id, cur);
    });
    return m;
  }, [optimizationResults, stops]);

  // Get stop color based on group.  Anchor stops override the zone colour
  // with a distinct near-black slate so they're immediately readable across
  // every zone they participate in.
  const getStopColor = (stop) => {
    // Grey out stops with no roster demand
    if (!stopsWithDemand.has(stop.id)) {
      return "#D1D5DB"; // grey-300
    }
    // Anchor stops → distinct near-black colour (outside GROUP_COLORS palette).
    if ((anchorZonesByStopId.get(stop.id) || new Set()).size > 0) {
      return "#0F172A"; // slate-900
    }
    if (stop.group_id) {
      const group = groups.find((g) => g.id === stop.group_id);
      return group?.color || "#94A3B8";
    }
    return "#94A3B8";
  };

  // Get ungrouped stops
  const ungroupedStops = stops.filter((s) => !s.group_id);
  
  // Stops with roster demand (at least 1 shift > 0)
  const stopsWithDemand = new Set(stops.filter(s => {
    const sd = s.shift_demands || {};
    return Object.values(sd).some(v => v > 0);
  }).map(s => s.id));
  const excludedStops = stops.filter(s => !stopsWithDemand.has(s.id));
  const ungroupedWithDemand = ungroupedStops.filter(s => stopsWithDemand.has(s.id));

  // Calculate map center
  const mapCenter = stops.length > 0
    ? [
        stops.reduce((sum, s) => sum + s.latitude, 0) / stops.length,
        stops.reduce((sum, s) => sum + s.longitude, 0) / stops.length,
      ]
    : [20.5937, 78.9629]; // Default to India center

  if (currentView === "comparison") {
    return (
      <TooltipProvider>
        <ComparisonView onBack={() => setCurrentView("planning")} />
        <Toaster position="bottom-right" richColors />
      </TooltipProvider>
    );
  }

  if (currentView === "settings") {
    return (
      <TooltipProvider>
        <SettingsPage onBack={() => setCurrentView("planning")} />
        <Toaster position="bottom-right" richColors />
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="app-container" data-testid="app-container">

        {/* ── BUID Setup Modal (blocks app until a client key is entered) ─── */}
        {showBuidModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            data-testid="buid-modal-overlay"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4">
              <div style={{ overflow: 'hidden', marginBottom: '12px', marginLeft: '-8px', marginRight: '-8px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_abef7da1-444b-465c-8a83-2cd66a76002d/artifacts/hw3dtw1t_MIS%20Horizontal.png"
                  alt="Logo"
                  style={{ height: '40px', display: 'block' }}
                />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Welcome to RouteOptima</h2>
              <p className="text-sm text-slate-500 mb-5">
                Enter your Business Unit ID (BUID) to get started. All your data will be scoped to this key.
              </p>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-1.5">
                Business Unit ID
              </label>
              <Input
                data-testid="buid-input"
                placeholder="e.g. tepljasmine-TN"
                value={buidInput}
                onChange={(e) => setBuidInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && buidInput.trim()) {
                    const v = buidInput.trim();
                    setBUID(v);
                    setBuid(v);
                    setShowBuidModal(false);
                  }
                }}
                className="mb-4"
                autoFocus
              />
              <Button
                data-testid="buid-confirm-btn"
                className="w-full"
                disabled={!buidInput.trim()}
                onClick={() => {
                  const v = buidInput.trim();
                  setBUID(v);
                  setBuid(v);
                  setShowBuidModal(false);
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* ── Switch BUID dialog ─────────────────────────────────────────── */}
        {showBuidSwitcher && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            data-testid="buid-switcher-overlay"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Switch Client</h2>
              <p className="text-sm text-slate-500 mb-5">
                Switching BUID will reload the app with the new client's data.
              </p>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-1.5">
                New BUID
              </label>
              <Input
                data-testid="buid-switch-input"
                placeholder="Enter new BUID"
                value={buidInput}
                onChange={(e) => setBuidInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && buidInput.trim()) {
                    const v = buidInput.trim();
                    setBUID(v);
                    window.location.reload();
                  }
                }}
                className="mb-4"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid="buid-switch-cancel"
                  onClick={() => {
                    setShowBuidSwitcher(false);
                    setBuidInput(getBUID());
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  data-testid="buid-switch-confirm"
                  disabled={!buidInput.trim() || buidInput.trim() === buid}
                  onClick={() => {
                    const v = buidInput.trim();
                    setBUID(v);
                    window.location.reload();
                  }}
                >
                  Switch & Reload
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Global task panel — floats in the top-right and reflects the
            single-flight server lock. While visible, all mutating buttons
            (uploads, Process & Validate, Auto-Cluster, Optimize) are
            disabled — this is the only visual cue ops gets that something
            is running elsewhere (other tab, scheduler, or API call). */}
        {activeTask && (
          <div
            className="fixed top-4 right-4 z-50 w-80 rounded-lg border border-amber-300 bg-amber-50 shadow-lg p-3"
            data-testid="active-task-panel"
          >
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="w-4 h-4 animate-spin text-amber-700" />
              <span className="font-semibold text-sm text-amber-900" data-testid="active-task-type">
                {String(activeTask.type || "task")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
            </div>
            <p className="text-xs text-amber-800 mb-2" data-testid="active-task-stage">
              {activeTask.stage || "Running…"}
            </p>
            <div className="w-full bg-amber-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-amber-600 h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.max(2, activeTask.progress || 0)}%` }}
                data-testid="active-task-progress-bar"
              />
            </div>
            <div className="flex justify-between text-[10px] text-amber-700 mt-1">
              <span>{activeTask.progress || 0}%</span>
              <span>started {activeTask.started_at ? new Date(activeTask.started_at).toLocaleTimeString() : ""}</span>
            </div>
            <p className="text-[10px] text-amber-700 mt-2 italic">
              Mutation actions are locked until this completes.
            </p>
          </div>
        )}

        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-header">
            <div style={{ overflow: 'hidden', marginBottom: '4px', marginLeft: '-8px', marginRight: '-8px' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_abef7da1-444b-465c-8a83-2cd66a76002d/artifacts/hw3dtw1t_MIS%20Horizontal.png" 
                alt="Logo" 
                style={{ height: '48px', display: 'block' }}
              />
            </div>
            <div className="flex items-center justify-between">
              <h1 className="font-heading text-xl font-bold tracking-tight text-foreground">
                RouteOptima
              </h1>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Demand Clubbing & Bus Allocation
            </p>
            {/* BUID badge */}
            {buid && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">Client:</span>
                  <span
                    className="text-xs font-mono font-semibold text-slate-700 truncate"
                    title={buid}
                    data-testid="buid-badge"
                  >
                    {buid}
                  </span>
                </div>
                <button
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium shrink-0 ml-2"
                  data-testid="buid-switch-btn"
                  onClick={() => {
                    setBuidInput(buid);
                    setShowBuidSwitcher(true);
                  }}
                >
                  Switch
                </button>
              </div>
            )}
          </div>

          <ScrollArea className="sidebar-content">
            {/* File Uploads */}
            <div className="section">
              <h2 className="section-title">Data Upload</h2>
              <div className="space-y-3">
                {/* Roster Date Range — must be set before uploading */}
                {uploadedFormat !== "demand" && (
                  <div className="border border-slate-200 rounded-md p-2.5 space-y-2 bg-slate-50/50">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Roster Date Range</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Start</label>
                        <input
                          type="date"
                          className="w-full h-7 px-2 text-xs border border-slate-200 rounded bg-white"
                          value={rosterStartDate}
                          onChange={(e) => {
                            setRosterStartDate(e.target.value);
                            handleSaveRosterDates(e.target.value, rosterEndDate);
                          }}
                          data-testid="roster-start-date"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">End</label>
                        <input
                          type="date"
                          className="w-full h-7 px-2 text-xs border border-slate-200 rounded bg-white"
                          value={rosterEndDate}
                          onChange={(e) => {
                            setRosterEndDate(e.target.value);
                            handleSaveRosterDates(rosterStartDate, e.target.value);
                          }}
                          data-testid="roster-end-date"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <FileUpload
                  label="Rosters / Demand"
                  endpoint="/upload/roster"
                  icon={Users}
                  onSuccess={(data) => {
                    const fmt = data?.format || "roster";
                    setUploadedFormat(fmt);
                    setCleaningDone(false);
                    setCleaningSummary(null);
                    fetchData();
                  }}
                  templateEndpoint="/template/roster"
                  downloadEndpoint="/download/roster"
                  hasData={rosterStatus.uploaded}
                  disabled={isTaskRunning}
                  disabledReason={activeTask ? `${activeTask.type} running — ${activeTask.stage}` : ""}
                />
                <FileUpload
                  label="Stops Master"
                  endpoint="/upload/stops"
                  icon={MapPin}
                  onSuccess={() => {
                    setCleaningSummary(null);
                    setCleaningDone(false);
                    fetchData();
                  }}
                  templateEndpoint="/template/stops"
                  downloadEndpoint="/download/stops"
                  hasData={stops.length > 0}
                  disabled={isTaskRunning}
                  disabledReason={activeTask ? `${activeTask.type} running — ${activeTask.stage}` : ""}
                />
                <FileUpload
                  label="Vehicle Fleet"
                  endpoint="/upload/vehicles"
                  icon={Bus}
                  onSuccess={fetchData}
                  templateEndpoint="/template/vehicles"
                  downloadEndpoint="/download/vehicles"
                  hasData={vehicles.length > 0}
                  disabled={isTaskRunning}
                  disabledReason={activeTask ? `${activeTask.type} running — ${activeTask.stage}` : ""}
                />
                <FileUpload
                  label="Shift Timings"
                  endpoint="/upload/shifts"
                  icon={Clock}
                  onSuccess={fetchData}
                  templateEndpoint="/template/shifts"
                  downloadEndpoint="/download/shifts"
                  hasData={shifts.length > 0}
                  disabled={isTaskRunning}
                  disabledReason={activeTask ? `${activeTask.type} running — ${activeTask.stage}` : ""}
                />
              </div>
            </div>

            {/* Office Location — moved below uploads */}
            <div className="section">
              <h2 className="section-title">Office Location</h2>
              {officeStop ? (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{officeStop.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {officeStop.latitude.toFixed(4)}, {officeStop.longitude.toFixed(4)}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleDeleteOffice}
                      data-testid="delete-office-btn"
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowOfficeDialog(true)}
                  data-testid="set-office-btn"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Set Office Location
                </Button>
              )}
            </div>

            {/* Process & Validate CTA — preview-then-commit flow:
                clicking the button calls /process-data/preview to compute
                what WILL happen, then opens a confirmation dialog showing
                past-date backfill counts vs active overwrites. The user
                can confirm or cancel before any data is mutated. */}
            <div className="section">
              <Button
                className="w-full"
                variant={cleaningDone ? "outline" : "default"}
                disabled={!rosterStatus.uploaded || stops.length === 0 || shifts.length === 0 || !officeStop || cleaningLoading || pvPreviewLoading || isTaskRunning}
                title={isTaskRunning ? `Another task is running: ${activeTask?.type}` : ""}
                onClick={async () => {
                  setPvPreviewLoading(true);
                  try {
                    const res = await axios.post(`${API}/process-data/preview`);
                    setPvPreview(res.data);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Preview failed");
                  } finally {
                    setPvPreviewLoading(false);
                  }
                }}
                data-testid="process-data-btn"
              >
                {pvPreviewLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading preview...
                  </>
                ) : cleaningLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : cleaningDone ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Re-process Data
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Process & Validate Data
                  </>
                )}
              </Button>
              {!rosterStatus.uploaded || stops.length === 0 || shifts.length === 0 || !officeStop ? (
                <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                  {[
                    !rosterStatus.uploaded && "Roster",
                    stops.length === 0 && "Stops",
                    shifts.length === 0 && "Shift Timings",
                    !officeStop && "Office Location"
                  ].filter(Boolean).join(", ")} required
                </p>
              ) : null}

              {/* Preview-before-commit dialog. Opens on Process & Validate
                  click after the dry-run preview returns. User confirms
                  to actually mutate the canonical rosters collection. */}
              <Dialog open={!!pvPreview} onOpenChange={(o) => { if (!o) setPvPreview(null); }}>
                <DialogContent className="sm:max-w-[520px]" data-testid="pv-preview-dialog">
                  <DialogHeader>
                    <DialogTitle>Confirm Process &amp; Validate</DialogTitle>
                    <DialogDescription>
                      Review what will change before committing. Past-date rosters
                      that already exist will not be overwritten.
                    </DialogDescription>
                  </DialogHeader>

                  {pvPreview && (
                    <div className="space-y-3 text-[12px]">
                      {/* Group 1: input volumes */}
                      <div className="rounded-md border border-slate-200 divide-y divide-slate-200">
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-slate-600">Uploaded rosters</span>
                          <span className="font-mono font-semibold" data-testid="pv-staging-rows">{pvPreview.staging_rows?.toLocaleString?.() ?? pvPreview.staging_rows}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-slate-600">Expanded (emp × date) rosters</span>
                          <span className="font-mono font-semibold" data-testid="pv-survivors">{pvPreview.survivors?.toLocaleString?.() ?? pvPreview.survivors}</span>
                        </div>
                      </div>

                      {/* Group 2: duplicates dropped */}
                      <div className="rounded-md border border-slate-200 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-amber-700">Duplicate rosters dropped</span>
                          <span className="font-mono font-semibold text-amber-700" data-testid="pv-duplicates">{pvPreview.duplicates_dropped?.toLocaleString?.() ?? pvPreview.duplicates_dropped}</span>
                        </div>
                      </div>

                      {/* Group 2b: rows missing emp_id (silently dropped — surfaced
                          so the user can see why their staging count and
                          expanded count don't reconcile) */}
                      {pvPreview.missing_emp_id_count > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50/50 px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-red-700">Rosters dropped — missing emp_id</span>
                            <span className="font-mono font-semibold text-red-700" data-testid="pv-missing-empid">{pvPreview.missing_emp_id_count?.toLocaleString?.() ?? pvPreview.missing_emp_id_count}</span>
                          </div>
                        </div>
                      )}

                      {/* Group 3: past-date handling */}
                      <div className="rounded-md border border-emerald-200 bg-emerald-50/50 divide-y divide-emerald-200">
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-emerald-800">Past-date rosters — new backfill</span>
                          <span className="font-mono font-semibold text-emerald-800" data-testid="pv-past-insert">{pvPreview.past_will_insert?.toLocaleString?.() ?? 0}</span>
                        </div>
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-emerald-800">Past-date rosters — preserved (not overwritten)</span>
                          <span className="font-mono font-semibold text-emerald-800" data-testid="pv-past-preserve">{pvPreview.past_will_preserve?.toLocaleString?.() ?? 0}</span>
                        </div>
                      </div>

                      {/* Group 4: today/future upserts */}
                      <div className="rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-blue-800">Today / future rosters — upsert (replaces existing)</span>
                          <span className="font-mono font-semibold text-blue-800" data-testid="pv-active-upsert">{pvPreview.active_will_upsert?.toLocaleString?.() ?? 0}</span>
                        </div>
                      </div>

                      {pvPreview.affected_dates?.length > 0 && (
                        <div className="text-[11px] text-slate-500">
                          <span className="font-medium">Dates affected:</span>{" "}
                          {pvPreview.affected_dates.length <= 7
                            ? pvPreview.affected_dates.join(", ")
                            : `${pvPreview.affected_dates[0]} → ${pvPreview.affected_dates[pvPreview.affected_dates.length - 1]} (${pvPreview.affected_dates.length} dates)`}
                        </div>
                      )}

                      {pvPreview.unmapped_count > 0 && (
                        <div className="text-[11px] text-red-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {pvPreview.unmapped_count} nodal points couldn't be mapped to a master stop.
                        </div>
                      )}

                      {pvPreview.fuzzy_match_skipped && (
                        <div className="text-[11px] text-slate-500 italic">
                          Mapping confidence (low/unmapped counts) is computed during commit.
                        </div>
                      )}
                    </div>
                  )}

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setPvPreview(null)}
                      disabled={cleaningLoading}
                      data-testid="pv-preview-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        setPvPreview(null);
                        setCleaningLoading(true);
                        setCleaningSummary(null);
                        setCleaningDone(false);
                        // Capture trigger time so the poll loop only treats
                        // the run as "done" when last_pv_run.completed_at
                        // is newer than this — prevents the loop from
                        // returning stale data from a previous failed run
                        // that left canonical rows behind.
                        const triggeredAt = new Date().toISOString();
                        try {
                          await axios.post(`${API}/process-data`);
                          // Poll for completion. setInterval continues to schedule
                          // ticks even when a previous tick is still awaiting the
                          // GET response — so two ticks could race and BOTH fire
                          // the success toast (the user reported the toast firing
                          // multiple times continuously). The `done` closure flag
                          // guards every tick: once we've seen a fresh completion,
                          // every other in-flight tick is a no-op.
                          let done = false;
                          const poll = setInterval(async () => {
                            if (done) return;
                            try {
                              const res = await axios.get(`${API}/roster-status`);
                              if (done) return;  // another tick won the race
                              const completedAt = res.data?.last_pv_run?.completed_at;
                              const isFreshRun = completedAt && completedAt > triggeredAt;
                              if (res.data?.cleaned && res.data?.total_rosters != null && isFreshRun) {
                                done = true;
                                clearInterval(poll);
                                setCleaningSummary({
                                  total_rosters: res.data.total_rosters,
                                  high_confidence_count: res.data.high_confidence_count || 0,
                                  low_confidence_count: res.data.low_confidence_count || 0,
                                  unmapped_count: res.data.unmapped_count || 0,
                                  duplicates_dropped_count: res.data.duplicates_dropped_count || 0,
                                  last_pv_run: res.data.last_pv_run || null,
                                });
                                setCleaningDone(true);
                                setCleaningLoading(false);
                                setRosterStatus(res.data);
                                if (res.data.shift_warning) {
                                  toast.error(res.data.shift_warning);
                                }
                                // Critical: surface silent persistence loss.
                                // If persistence_gap > 0, the bulk_write
                                // accepted fewer rows than survivors —
                                // typically a stale unique index. The user
                                // would otherwise see "9406 processed" with
                                // no idea why.
                                const gap = res.data.last_pv_run?.persistence_gap || 0;
                                const writeErrors = res.data.last_pv_run?.write_errors || 0;
                                if (gap > 0) {
                                  toast.error(
                                    `${gap} rows did not persist to the database (${writeErrors} write errors). ` +
                                    `Likely a stale index on db.rosters — please redeploy to trigger the index self-heal.`,
                                    { duration: 15000 }
                                  );
                                }
                                const dupCount = res.data.duplicates_dropped_count || 0;
                                const lines = [
                                  `Processed ${res.data.total_rosters} roster entries for ${res.data.start_date} → ${res.data.end_date}.`,
                                ];
                                if (dupCount > 0) {
                                  lines.push(`${dupCount} duplicate rows dropped — download from the Nodal Point Mapping card.`);
                                }
                                toast.success(lines.join(" "));
                                fetchData();
                              }
                            } catch (e) { /* ignore */ }
                          }, 2000);
                          setTimeout(() => { done = true; clearInterval(poll); setCleaningLoading(false); }, 120000);
                        } catch (err) {
                          setCleaningLoading(false);
                          toast.error(err.response?.data?.detail || "Processing failed");
                        }
                      }}
                      data-testid="pv-preview-confirm-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Confirm &amp; Process
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Nodal Point Mapping Stats — shown after processing */}
            {cleaningDone && cleaningSummary && uploadedFormat !== "demand" && (
              <div className="section">
                <h2 className="section-title">Nodal Point Mapping</h2>
                <div className="space-y-2">
                  <div className="rounded-md border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-4 text-center text-[10px] divide-x divide-slate-200">
                      <div className="py-1.5 bg-slate-50">
                        <div className="font-semibold text-slate-700" data-testid="total-rosters-count">{cleaningSummary.total_rosters}</div>
                        <div className="text-muted-foreground leading-tight">Total Rosters</div>
                      </div>
                      <div className="py-1.5 bg-green-50">
                        <div className="font-semibold text-green-700" data-testid="high-confidence-count">{cleaningSummary.high_confidence_count}</div>
                        <div className="text-green-600 leading-tight">High Confidence</div>
                      </div>
                      <div className="py-1.5 bg-amber-50">
                        <div className="font-semibold text-amber-700" data-testid="low-confidence-count">{cleaningSummary.low_confidence_count}</div>
                        <div className="text-amber-600 leading-tight">Low Confidence</div>
                      </div>
                      <div className="py-1.5 bg-red-50">
                        <div className="font-semibold text-red-700" data-testid="unmapped-count">{cleaningSummary.unmapped_count}</div>
                        <div className="text-red-600 leading-tight">Unmapped</div>
                      </div>
                    </div>
                  </div>

                  {/* Per-stage timing line — surfaces fuzzy-cache hit ratio
                      and stage breakdown so regressions are visible without
                      digging into logs. Hidden when timings aren't available
                      (older runs before this was added). */}
                  {cleaningSummary.last_pv_run?.timings && (
                    <div
                      className="text-[10px] text-slate-500 leading-tight px-1"
                      data-testid="pv-timings"
                    >
                      Completed in <span className="font-mono font-semibold text-slate-700">{(cleaningSummary.last_pv_run.timings.total || 0).toFixed(1)}s</span>
                      {" — "}
                      {(cleaningSummary.last_pv_run.timings.fuzzy || 0).toFixed(1)}s fuzzy
                      {cleaningSummary.last_pv_run.timings.fuzzy_cache_hits > 0 && cleaningSummary.last_pv_run.timings.fuzzy_fresh === 0 && " (cached)"}
                      {cleaningSummary.last_pv_run.timings.fuzzy_fresh > 0 && ` (${cleaningSummary.last_pv_run.timings.fuzzy_cache_hits} cached, ${cleaningSummary.last_pv_run.timings.fuzzy_fresh} fresh)`}
                      {", "}
                      {(cleaningSummary.last_pv_run.timings.expand || 0).toFixed(1)}s expand
                      {", "}
                      {(cleaningSummary.last_pv_run.timings.dedupe || 0).toFixed(1)}s dedupe
                      {", "}
                      {(cleaningSummary.last_pv_run.timings.upsert || 0).toFixed(1)}s upsert
                    </div>
                  )}

                  {/* Duplicates banner — only rendered when there were drops in the last P&V */}
                  {(cleaningSummary.duplicates_dropped_count || 0) > 0 && (
                    <div
                      className="flex items-center justify-between gap-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5 text-[11px]"
                      data-testid="duplicates-banner"
                    >
                      <div className="flex items-center gap-1.5 text-orange-800">
                        <AlertTriangle className="w-3 h-3" />
                        <span>
                          <span className="font-semibold" data-testid="duplicates-dropped-count">
                            {cleaningSummary.duplicates_dropped_count}
                          </span>{" "}
                          duplicate rows dropped (first row per employee-date kept)
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[10px] border-orange-300 text-orange-800 hover:bg-orange-100"
                        onClick={handleDownloadRosterDuplicates}
                        disabled={duplicatesDownloading}
                        data-testid="download-duplicates-btn"
                      >
                        {duplicatesDownloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                        Download
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-7 text-[11px]"
                    onClick={handleDownloadCleanedRoster}
                    disabled={cleanedRosterDownloading}
                    data-testid="download-cleaned-roster-btn"
                  >
                    {cleanedRosterDownloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                    {cleanedRosterDownloading ? "Downloading..." : "Download Cleaned Roster"}
                  </Button>
                </div>
              </div>
            )}

            {/* Cluster Source */}
            <div className="section">
              <div className="text-xs font-medium text-muted-foreground mb-2">Cluster Source</div>
              <div className="flex gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="cluster-mode-toggle">
                <button
                  onClick={() => handleClusterModeChange("last_saved")}
                  disabled={!clusterCacheExists}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    clusterMode === "last_saved"
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  } ${!clusterCacheExists ? "opacity-40 cursor-not-allowed" : ""}`}
                  title={!clusterCacheExists ? "No saved clusters yet — Fresh Run will be used" : ""}
                  data-testid="cluster-mode-last-saved"
                >
                  <Save className="w-3 h-3" />
                  Last Saved
                </button>
                <button
                  onClick={() => handleClusterModeChange("fresh_run")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    clusterMode === "fresh_run"
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="cluster-mode-fresh-run"
                >
                  <RefreshCw className="w-3 h-3" />
                  Fresh Run
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {clusterMode === "last_saved"
                  ? "Reuses saved clusters + auto-assigns any new stops to the nearest zone."
                  : "Recomputes clusters from scratch. Any manual edits will be lost."}
              </p>
            </div>

            {/* Route Mode */}
            <div className="section">
              <div className="text-xs font-medium text-muted-foreground mb-2">Route Mode</div>
              <div className="flex gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="route-mode-toggle">
                <button
                  onClick={() => { setRouteMode("dynamic"); setHasOptimized(false); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    routeMode === "dynamic"
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="route-mode-dynamic"
                >
                  <Shuffle className="w-3 h-3" />
                  Dynamic
                </button>
                <button
                  onClick={() => { setRouteMode("static"); setHasOptimized(false); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    routeMode === "static"
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="route-mode-static"
                >
                  <Lock className="w-3 h-3" />
                  Static
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {routeMode === "dynamic"
                  ? "Routes optimized per shift based on demand."
                  : "Same routes across all shifts (uses peak demand)."}
              </p>
            </div>

            {/* Fleet Mix */}
            <div className="section">
              <div className="text-xs font-medium text-muted-foreground mb-2">Fleet Mix</div>
              <div className="flex gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="fleet-mix-toggle">
                <button
                  onClick={() => { setFleetMix("constrained"); setHasOptimized(false); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    fleetMix === "constrained"
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="fleet-mix-constrained"
                >
                  <Lock className="w-3 h-3" />
                  Constrained
                </button>
                <button
                  onClick={() => { setFleetMix("ideal"); setHasOptimized(false); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    fleetMix === "ideal"
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="fleet-mix-ideal"
                >
                  <BarChart3 className="w-3 h-3" />
                  Ideal
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {fleetMix === "constrained"
                  ? "Uses your uploaded vehicle fleet."
                  : "Finds optimal mix from all available capacities (3-50 seats)."}
              </p>
            </div>

            {/* Roster Adjuster */}
            <div className="section">
              <h2 className="section-title">Roster Adjuster</h2>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {demandAdjustment < 0 ? (
                      <TrendingDown className="w-4 h-4 text-blue-600" />
                    ) : demandAdjustment > 0 ? (
                      <TrendingUp className="w-4 h-4 text-orange-600" />
                    ) : (
                      <Users className="w-4 h-4 text-slate-500" />
                    )}
                    <span className="text-sm font-medium">Adjustment</span>
                  </div>
                  <span
                    className={`font-mono font-semibold text-lg ${
                      demandAdjustment < 0
                        ? "text-blue-600"
                        : demandAdjustment > 0
                        ? "text-orange-600"
                        : "text-slate-600"
                    }`}
                    data-testid="demand-adjustment-value"
                  >
                    {demandAdjustment > 0 ? "+" : ""}{demandAdjustment}%
                  </span>
                </div>
                <Slider
                  value={[demandAdjustment]}
                  onValueChange={(value) => {
                    setDemandAdjustment(value[0]);
                    setHasOptimized(false);
                  }}
                  min={-100}
                  max={100}
                  step={5}
                  className="mb-3"
                  data-testid="demand-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>-100%</span>
                  <span>Base</span>
                  <span>+100%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Adjust roster demand to account for leave, holidays, or special events.
                </p>
              </div>
            </div>

            {/* Generate Routes CTA + inline notifications */}
            <div className="section">
              {excludedStops.length > 0 && (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="excluded-stops-count">
                  {excludedStops.length} stop{excludedStops.length !== 1 ? 's' : ''} excluded (no roster demand)
                </div>
              )}
              {partialClusterInfo && (partialClusterInfo.assigned_count > 0 || partialClusterInfo.unassigned_count > 0) && (
                <div
                  className="mb-3 text-xs bg-amber-50 border border-amber-300 rounded px-2.5 py-2 space-y-1"
                  data-testid="partial-cluster-banner"
                >
                  {partialClusterInfo.assigned_count > 0 && (
                    <div className="text-amber-800">
                      <span className="font-semibold">{partialClusterInfo.assigned_count}</span> new stop{partialClusterInfo.assigned_count !== 1 ? "s" : ""} auto-assigned to nearest zone.
                    </div>
                  )}
                  {partialClusterInfo.unassigned_count > 0 && (
                    <div className="text-amber-900">
                      <span className="font-semibold">{partialClusterInfo.unassigned_count}</span> stop{partialClusterInfo.unassigned_count !== 1 ? "s" : ""} too far (&gt;15 km) from every zone. Please assign them manually from the map or switch <b>Cluster Source</b> to <b>Fresh Run</b> and re-run.
                    </div>
                  )}
                  <button
                    className="text-xs text-amber-700 hover:text-amber-900 underline"
                    onClick={() => setPartialClusterInfo(null)}
                    data-testid="dismiss-partial-cluster-banner"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <Button
                className="w-full"
                onClick={handleOptimize}
                disabled={loading || stops.length === 0 || isTaskRunning}
                title={isTaskRunning ? `Another task is running: ${activeTask?.type}` : ""}
                data-testid="optimize-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : hasOptimized ? (
                  <Eye className="w-4 h-4 mr-2" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {hasOptimized ? "See Routes" : "Generate Routes"}
              </Button>
              {loading && (
                <div className="mt-2 space-y-1" data-testid="optimization-progress">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${optimizationProgress.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{optimizationProgress.stage}</span>
                    <span>{optimizationProgress.progress}%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Groups — visible only AFTER routes are generated */}
            {hasOptimized && (
            <div className="section">
              <h2 className="section-title">Groups ({groups.length})</h2>
              {groups.length > 0 && (
                <div className="flex gap-1 mb-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedZoneIds(new Set([...groups.map(g => g.id), "__ungrouped__", "__excluded__"]))}
                    className="flex-1 h-7 text-xs"
                    data-testid="show-all-zones-btn"
                  >
                    Show All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedZoneIds(new Set())}
                    disabled={selectedZoneIds.size === 0}
                    className="flex-1 h-7 text-xs"
                    data-testid="unselect-all-zones-btn"
                  >
                    Unselect All
                  </Button>
                </div>
              )}
              {ungroupedWithDemand.length > 0 && groups.length > 0 && (
                <div
                  className={`flex items-center justify-between px-2 py-1.5 rounded text-xs cursor-pointer transition-all mb-1 border ${
                    selectedZoneIds.has("__ungrouped__") ? "ring-2 ring-offset-1 ring-blue-500 bg-blue-50 border-slate-200" : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                  }`}
                  onClick={() => setSelectedZoneIds(prev => {
                    const next = new Set(prev);
                    if (next.has("__ungrouped__")) next.delete("__ungrouped__");
                    else next.add("__ungrouped__");
                    return next;
                  })}
                  data-testid="ungrouped-stops-toggle"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#94A3B8" }} />
                    <span className="font-medium">Ungrouped</span>
                  </div>
                  <span className="text-muted-foreground">{ungroupedWithDemand.length} stops</span>
                </div>
              )}
              {excludedStops.length > 0 && groups.length > 0 && (
                <div
                  className={`flex items-center justify-between px-2 py-1.5 rounded text-xs cursor-pointer transition-all mb-2 border ${
                    selectedZoneIds.has("__excluded__") ? "ring-2 ring-offset-1 ring-amber-500 bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                  }`}
                  onClick={() => setSelectedZoneIds(prev => {
                    const next = new Set(prev);
                    if (next.has("__excluded__")) next.delete("__excluded__");
                    else next.add("__excluded__");
                    return next;
                  })}
                  data-testid="excluded-stops-toggle"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#D1D5DB" }} />
                    <span className="font-medium">Excluded</span>
                  </div>
                  <span className="text-muted-foreground">{excludedStops.length} stops</span>
                </div>
              )}
              {groups.length === 0 ? (
                <div className="empty-state">
                  <Layers className="empty-state-icon" />
                  <p className="text-sm">No groups yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {groups.map((group) => {
                    const groupStops = stops.filter((s) => s.group_id === group.id);
                    // Actual carried demand (post-anchor absorption):
                    //   ─ before optimization ⇒ raw sum of peak shift_demands
                    //     across group stops (best pre-run estimate).
                    //   ─ after optimization  ⇒ sum of total_passengers on
                    //     this zone's bus_routes.  This correctly reflects:
                    //       (a) home zones LOSE demand carried away by
                    //           other zones via anchor absorption.
                    //       (b) absorbing zones GAIN the absorbed portion.
                    //     Uses top-level bus_routes (peak snapshot) so the
                    //     sidebar always shows peak-equivalent load.
                    const getDemandByShift = () => {
                      if (hasOptimized && optimizationResults?.results) {
                        const zr = optimizationResults.results.find(
                          (r) => r.entity_type === "group" && r.entity_id === group.id
                        );
                        if (zr) {
                          return (zr.bus_routes || []).reduce(
                            (acc, br) => acc + (br.total_passengers || 0), 0
                          );
                        }
                      }
                      const agg = {};
                      groupStops.forEach(s => {
                        Object.entries(s.shift_demands || {}).forEach(([k, v]) => {
                          agg[k] = (agg[k] || 0) + v;
                        });
                      });
                      const vals = Object.values(agg);
                      return vals.length > 0 ? Math.max(...vals) : 0;
                    };
                    const totalDemand = getDemandByShift();
                    const demandLabel = hasOptimized ? "Carried" : "Peak";
                    return (
                      <div key={group.id} className={`group-item cursor-pointer transition-all ${selectedZoneIds.has(group.id) ? 'ring-2 ring-offset-1 ring-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`} data-testid={`group-${group.id}`}
                        onClick={() => setSelectedZoneIds(prev => {
                          const next = new Set(prev);
                          if (next.has(group.id)) next.delete(group.id);
                          else next.add(group.id);
                          return next;
                        })}
                      >
                        <div
                          className="group-color"
                          style={{ backgroundColor: group.color }}
                        />
                        <div className="group-info flex-1 min-w-0">
                          {editingGroupId === group.id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={editGroupName}
                                onChange={(e) => setEditGroupName(e.target.value)}
                                className="h-7 text-sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameGroup(group.id);
                                  if (e.key === 'Escape') {
                                    setEditingGroupId(null);
                                    setEditGroupName("");
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleRenameGroup(group.id)}
                              >
                                Save
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="group-name">{group.name}</div>
                              <div className="group-stops">
                                {groupStops.length} stops | {demandLabel}: {totalDemand}
                              </div>
                            </>
                          )}
                        </div>
                        {editingGroupId !== group.id && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setEditingGroupId(group.id);
                                    setEditGroupName(group.name);
                                  }}
                                  data-testid={`edit-group-${group.id}`}
                                >
                                  <Pencil className="w-4 h-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Rename group</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleDeleteGroup(group.id)}
                                  data-testid={`delete-group-${group.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete group</TooltipContent>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Post-run actions */}
            <div className="section">
              <Button
                variant="outline"
                className="w-full"
                disabled={!optimizationResults}
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/individual-vehicles`);
                    setIndividualVehicles(res.data);
                  } catch { /* ignore */ }
                  setShowAssignBuses(true);
                }}
                data-testid="assign-buses-btn"
              >
                <Bus className="w-4 h-4 mr-2" />
                Assign Vehicles
              </Button>
              <Button
                variant="outline"
                className="w-full mt-2"
                disabled={!optimizationResults || exportLoading}
                onClick={handleExportRoutes}
                data-testid="export-routes-btn"
              >
                {exportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {exportLoading ? "Downloading..." : "Download Optimization Results"}
              </Button>
              <Button
                variant="outline"
                className="w-full mt-2"
                disabled={!optimizationResults || tripsheetDownloading}
                onClick={handleDownloadTripsheet}
                data-testid="download-tripsheet-btn"
              >
                {tripsheetDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                {tripsheetDownloading ? "Downloading..." : "Download Tripsheet"}
              </Button>
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => setCurrentView("comparison")}
                data-testid="switch-to-comparison-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Compare
              </Button>
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => setCurrentView("settings")}
                data-testid="switch-to-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </ScrollArea>
        </aside>

        {/* Map Container */}
        <main className="map-container">
          <MapContainer
            center={mapCenter}
            zoom={10}
            style={{ height: "100%", width: "100%" }}
            data-testid="map-container"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapBoundsUpdater stops={stops} />
            <MapSearchControl stops={stops} />

            {stops.filter(s => {
              const hasDemand = stopsWithDemand.has(s.id);
              if (!hasDemand) return selectedZoneIds.has("__excluded__");
              // Anchor stops appear whenever ANY of their home/anchor zones
              // is selected in the filter (so they're visible from every
              // zone they participate in).
              const anchorZones = anchorZonesByStopId.get(s.id);
              if (anchorZones && anchorZones.size > 0) {
                for (const z of anchorZones) {
                  if (selectedZoneIds.has(z)) return true;
                }
              }
              if (!s.group_id) return selectedZoneIds.has("__ungrouped__");
              return selectedZoneIds.has(s.group_id);
            }).map((stop) => (
              <Marker
                key={stop.id}
                position={[stop.latitude, stop.longitude]}
                icon={createMarkerIcon(
                  getStopColor(stop),
                  selectedStopIds.has(stop.id)
                )}
                eventHandlers={{
                  click: () => {
                    if (!stop.group_id) {
                      toggleStopSelection(stop.id);
                    }
                  },
                }}
              >
                <Popup>
                  <div className="demand-tooltip min-w-[200px]">
                    <div className="font-semibold mb-2">{stop.stop_name}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                      {Object.entries(stop.shift_demands || {}).sort(([a], [b]) => a.localeCompare(b)).map(([sk, val]) => (
                        <div key={sk}>
                          <div className="text-muted-foreground">Shift {sk}</div>
                          <div className="font-mono font-semibold">{val || 0}</div>
                        </div>
                      ))}
                      {(!stop.shift_demands || Object.keys(stop.shift_demands).length === 0) && (
                        <div className="col-span-3 text-muted-foreground">No demand data</div>
                      )}
                    </div>
                    <div className="border-t pt-2 space-y-3">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Home Zone</div>
                        <Select
                          value={stop.group_id || "none"}
                          onValueChange={(value) => handleUpdateStopGroup(stop.id, value === "none" ? null : value)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`popup-home-zone-${stop.id}`}>
                            <SelectValue placeholder="Select group" />
                          </SelectTrigger>
                          <SelectContent className="z-[10000]">
                            <SelectItem value="none">
                              <span className="text-muted-foreground">No group (ungrouped)</span>
                            </SelectItem>
                            {groups.map((group) => (
                              <SelectItem key={group.id} value={group.id}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: group.color }}
                                  />
                                  {group.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Force-anchor multi-select — additional zones whose
                          bus routes MUST also pick this stop up on the next
                          Generate Routes.  Home zone is excluded from the
                          candidate list to enforce the same rule the
                          backend validates. */}
                      {stop.group_id && groups.length > 1 && (
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <span>Force as Anchor in Zones</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-slate-400 cursor-help">ⓘ</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[240px]">
                                Selected zones&apos; bus routes will be forced to pick this stop up on the next Generate Routes.  Bypasses corridor/demand gates.
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <div className="max-h-40 overflow-y-auto border rounded p-1.5 space-y-1 bg-slate-50" data-testid={`popup-anchor-zones-${stop.id}`}>
                            {groups.filter((g) => g.id !== stop.group_id).map((group) => {
                              const selected = (stop.forced_anchor_zone_ids || []).includes(group.id);
                              return (
                                <label
                                  key={group.id}
                                  className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer transition-all ${
                                    selected ? "bg-slate-900 text-white" : "hover:bg-slate-100"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="accent-slate-900"
                                    checked={selected}
                                    onChange={(e) => {
                                      const current = new Set(stop.forced_anchor_zone_ids || []);
                                      if (e.target.checked) current.add(group.id);
                                      else current.delete(group.id);
                                      handleUpdateStopAnchorZones(stop.id, Array.from(current));
                                    }}
                                    data-testid={`popup-anchor-checkbox-${stop.id}-${group.id}`}
                                  />
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: group.color }}
                                  />
                                  <span className="truncate">{group.name}</span>
                                </label>
                              );
                            })}
                          </div>
                          {(stop.forced_anchor_zone_ids || []).length > 0 && (
                            <button
                              className="mt-1 text-xs text-slate-500 hover:text-slate-800 underline"
                              onClick={() => handleUpdateStopAnchorZones(stop.id, [])}
                              data-testid={`popup-clear-anchors-${stop.id}`}
                            >
                              Clear all anchors
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Office Marker */}
            {officeStop && (
              <Marker
                key="office"
                position={[officeStop.latitude, officeStop.longitude]}
                icon={createOfficeMarkerIcon()}
                zIndexOffset={1000}
              >
                <Popup>
                  <div className="demand-tooltip">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold">{officeStop.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <div>Lat: {officeStop.latitude.toFixed(6)}</div>
                      <div>Lng: {officeStop.longitude.toFixed(6)}</div>
                    </div>
                    <div className="mt-2 pt-2 border-t">
                      <Badge className="bg-blue-600 text-white text-xs">Office Location</Badge>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Heatmap Layer */}
            <HeatmapLayer 
              stops={stops} 
              shiftFilter={heatmapShift} 
              visible={showHeatmap} 
            />

            {/* Route Polylines - Show actual road routes when entity is selected (hidden during focused route view) */}
            {selectedRouteEntity && selectedRouteEntity.bus_routes && officeStop && !focusedRoute && (
              <>
                {loadingRoutes && (
                  <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-[1000] bg-white px-4 py-2 rounded-lg shadow-lg">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading routes...</span>
                    </div>
                  </div>
                )}
                {mapDisplayedRoutes.map((route, routeIdx) => {
                  const routeKey = `${selectedRouteEntity.entity_id}-${route.bus_id}`;
                  const routeGeometry = routeGeometries[routeKey];
                  const routeColor = route.route_type === "dedicated" ? "#3B82F6" : "#F59E0B";
                  
                  // If we have actual route geometry, use it
                  if (routeGeometry && routeGeometry.coordinates) {
                    return (
                      <ActualRoutePolyline
                        key={`route-${route.bus_id}-${routeIdx}`}
                        route={route}
                        routeGeometry={routeGeometry}
                        entityId={selectedRouteEntity.entity_id}
                        color={routeColor}
                      />
                    );
                  }
                  
                  // Fallback to straight lines while loading
                  const routePositions = [
                    [officeStop.latitude, officeStop.longitude],
                    ...route.stops.map(stop => [stop.latitude, stop.longitude]),
                    [officeStop.latitude, officeStop.longitude]
                  ];
                  
                  return (
                    <Polyline
                      key={`route-${route.bus_id}-${routeIdx}`}
                      positions={routePositions}
                      color={routeColor}
                      weight={2}
                      opacity={0.4}
                      dashArray="5, 10"
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="font-semibold">{route.bus_id}</div>
                          <div className="text-xs text-muted-foreground">Loading actual route...</div>
                        </div>
                      </Popup>
                    </Polyline>
                  );
                })}
              </>
            )}
            {/* Single Focused Route */}
            {focusedRoute && focusedRoute.geometry && (
              <>
                {/* OSRM Route Polyline */}
                {focusedRoute.geometry.coordinates && (
                  <Polyline
                    positions={
                      focusedRoute.geometry.is_fallback
                        ? focusedRoute.geometry.coordinates.map(c => [c[1], c[0]])
                        : focusedRoute.geometry.coordinates.map(c => [c[1], c[0]])
                    }
                    color={focusedRoute.color}
                    weight={4}
                    opacity={0.85}
                  />
                )}
                {/* Numbered Stop Markers */}
                {focusedRoute.route.stops?.map((stop, idx) => (
                  <Marker
                    key={`focused-stop-${idx}`}
                    position={[stop.latitude, stop.longitude]}
                    icon={L.divIcon({
                      className: "focused-stop-marker",
                      html: `<div style="
                        width:24px;height:24px;border-radius:50%;
                        background:${focusedRoute.color};color:white;
                        display:flex;align-items:center;justify-content:center;
                        font-size:11px;font-weight:700;
                        border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);
                      ">${idx + 1}</div>`,
                      iconSize: [24, 24],
                      iconAnchor: [12, 12],
                    })}
                    zIndexOffset={500}
                  >
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">#{idx + 1} — {stop.stop_name}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {Object.entries(stop.pickup_times || {}).map(([s, t]) => (
                            <div key={s}>Shift {s}: {t}</div>
                          ))}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {/* Auto-fit map to focused route */}
                <FocusedRouteFitter route={focusedRoute} officeStop={officeStop} />
              </>
            )}

          </MapContainer>

          {/* Focused Route Floating Card */}
          {focusedRoute && (
            <div
              className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-xl border px-4 py-3 min-w-[320px]"
              data-testid="focused-route-card"
            >
              <div className="flex items-center justify-between gap-4">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  disabled={focusedRoute.currentIndex <= 0}
                  onClick={() => navigateRoute(-1)}
                  data-testid="prev-route-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-center flex-1">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: focusedRoute.color }} />
                    <span className="font-semibold text-sm">{focusedRoute.route.bus_id}</span>
                    <span className="text-xs text-muted-foreground">
                      ({focusedRoute.entity.entity_name})
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {focusedRoute.shiftIdle ? (
                      <span className="text-amber-600 font-medium" data-testid="focused-shift-idle-msg">
                        No trip in shift {focusedShift} — bus idle
                      </span>
                    ) : (
                      <>
                        {focusedRoute.route.stops?.length} stops
                        {focusedRoute.geometry?.distance ? ` · ${(focusedRoute.geometry.distance / 1000).toFixed(1)} km` : ""}
                        {focusedRoute.geometry?.duration ? ` · ${Math.round(focusedRoute.geometry.duration / 60)} min` : ""}
                      </>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Route {(focusedRoute.currentIndex || 0) + 1} of {focusedRoute.allRoutes?.length || "?"}
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  disabled={focusedRoute.currentIndex >= (focusedRoute.allRoutes?.length || 1) - 1}
                  onClick={() => navigateRoute(1)}
                  data-testid="next-route-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8"
                  onClick={() => {
                    setFocusedRoute(null);
                    setShowResults(true);
                  }}
                  data-testid="back-to-results-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Close
                </Button>
              </div>
              {/* Shift filter — show this bus's route for a specific shift */}
              {(() => {
                const shiftKeys = Object.keys(focusedRoute.entity.shift_deployments || {}).sort();
                if (shiftKeys.length === 0) return null;
                const busId = (focusedRoute.baseRoute || focusedRoute.route).bus_id;
                const hasTrip = (k) => {
                  const r = resolveRouteForShift(focusedRoute.entity, busId, k);
                  return r && r.stops?.length > 0 && (r.total_passengers || 0) > 0 && r.status !== "Idle";
                };
                return (
                  <div
                    className="flex items-center justify-center gap-1 mt-2 pt-2 border-t"
                    data-testid="focused-shift-selector"
                  >
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mr-1">
                      Shift
                    </span>
                    {["peak", ...shiftKeys].map((k) => (
                      <button
                        key={k}
                        onClick={() => switchFocusedShift(k)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                          focusedShift === k
                            ? "bg-slate-900 text-white"
                            : k !== "peak" && !hasTrip(k)
                              ? "bg-slate-50 text-slate-300 hover:bg-slate-100"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        title={k !== "peak" && !hasTrip(k) ? `${busId} has no trip in shift ${k}` : ""}
                        data-testid={`focused-shift-${k}`}
                      >
                        {k === "peak" ? "Peak" : k}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Focused Route Loading */}
          {focusedRouteLoading && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-white px-4 py-2 rounded-lg shadow-lg">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading route...</span>
              </div>
            </div>
          )}

          {/* Selection Action Panel - Fixed at top of map area */}
          {selectedStopIds.size > 0 && (
            <div 
              className="absolute top-4 left-4 right-4 z-[1000] flex justify-center pointer-events-none"
              data-testid="selection-bar-wrapper"
            >
              <div 
                className="bg-slate-900 text-white px-6 py-4 rounded-lg shadow-2xl border border-slate-700 pointer-events-auto"
                data-testid="selection-bar"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-500 rounded-full w-10 h-10 flex items-center justify-center">
                      <span className="font-mono text-lg font-bold">{selectedStopIds.size}</span>
                    </div>
                    <span className="text-sm font-medium">stops selected</span>
                  </div>
                  <div className="h-8 w-px bg-slate-600" />
                  <Button
                    size="lg"
                    onClick={() => setShowGroupDialog(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6"
                    data-testid="group-btn"
                  >
                    <Layers className="w-5 h-5 mr-2" />
                    Create Group
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setSelectedStopIds(new Set())}
                    className="text-slate-400 hover:text-white hover:bg-slate-700 h-10 w-10"
                    data-testid="clear-selection-btn"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Heatmap Control Panel */}
          <div className="absolute bottom-4 left-4 z-[1000]" data-testid="heatmap-controls">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-3">
              <div className="flex items-center gap-3">
                <Button
                  variant={showHeatmap ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowHeatmap(!showHeatmap)}
                  className={showHeatmap ? "bg-purple-500 hover:bg-purple-600" : ""}
                  data-testid="heatmap-toggle"
                >
                  <Flame className="w-4 h-4 mr-1" />
                  Demand Heatmap
                </Button>
                {showHeatmap && (
                  <div className="flex items-center gap-1 border-l pl-3">
                    <span className="text-xs text-muted-foreground mr-1">Shift:</span>
                    {["peak", ...(() => {
                      const allShifts = new Set();
                      stops.forEach(s => Object.keys(s.shift_demands || {}).forEach(k => allShifts.add(k)));
                      return [...allShifts].sort();
                    })()].map((shift) => (
                      <Button
                        key={shift}
                        variant={heatmapShift === shift ? "default" : "ghost"}
                        size="sm"
                        className={`h-7 px-2 text-xs ${heatmapShift === shift ? "bg-slate-800" : ""}`}
                        onClick={() => setHeatmapShift(shift)}
                        data-testid={`heatmap-shift-${shift}`}
                      >
                        {shift === "peak" ? "Peak" : shift}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              {showHeatmap && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                  <span className="text-xs text-muted-foreground">Size = Demand:</span>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-purple-500 opacity-50" />
                    <span className="text-xs text-muted-foreground">Low</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 rounded-full bg-purple-500 opacity-50" />
                    <span className="text-xs text-muted-foreground">Med</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-7 h-7 rounded-full bg-purple-500 opacity-50" />
                    <span className="text-xs text-muted-foreground">High</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Floating Results Panel */}
          {optimizationResults && showResults && (
            <div className="floating-panel floating-panel-top-right" data-testid="results-panel">
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Optimization Results</h3>
                  {optimizationResults.summary.demand_adjustment !== 0 && (
                    <span className={`text-xs font-mono ${
                      optimizationResults.summary.demand_adjustment > 0 
                        ? "text-orange-600" 
                        : "text-blue-600"
                    }`}>
                      Demand: {optimizationResults.summary.demand_adjustment > 0 ? "+" : ""}
                      {optimizationResults.summary.demand_adjustment}%
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setShowVehicleChains(true)}
                    data-testid="btn-vehicle-chains"
                  >
                    Vehicle Chains
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setShowResults(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="stats-card">
                    <div className="stats-value text-lg" data-testid="total-routes">
                      {optimizationResults.summary.total_routes || optimizationResults.summary.total_buses}
                    </div>
                    <div className="stats-label">Routes</div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-value text-lg" data-testid="total-vehicles">
                      {optimizationResults.summary.total_vehicles || optimizationResults.summary.total_buses}
                    </div>
                    <div className="stats-label">Vehicles</div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-value text-lg" data-testid="utilization">
                      {optimizationResults.summary.overall_utilization}%
                    </div>
                    <div className="stats-label">Occupancy</div>
                  </div>
                </div>

                {/* Fleet-balance status (Constrained + Static only) */}
                {optimizationResults.summary.fleet_balance?.active && (
                  <div
                    className={`rounded-md border p-2 mb-3 text-xs ${
                      optimizationResults.summary.fleet_balance.within_fleet
                        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                        : "bg-amber-50 border-amber-200 text-amber-900"
                    }`}
                    data-testid="fleet-balance-status"
                  >
                    <div className="font-semibold mb-0.5">Fleet Balancing</div>
                    {optimizationResults.summary.fleet_balance.within_fleet ? (
                      optimizationResults.summary.fleet_balance.removed_buses > 0 ? (
                        <div>
                          Dropped {optimizationResults.summary.fleet_balance.removed_buses} underutilized bus
                          {optimizationResults.summary.fleet_balance.removed_buses === 1 ? "" : "es"},
                          moved {optimizationResults.summary.fleet_balance.stops_moved} stop
                          {optimizationResults.summary.fleet_balance.stops_moved === 1 ? "" : "s"}.
                          Fleet count respected ({optimizationResults.summary.fleet_balance.total_buses_after}/{optimizationResults.summary.fleet_balance.fleet_count}).
                        </div>
                      ) : (
                        <div>
                          Within fleet count ({optimizationResults.summary.fleet_balance.total_buses_after}/{optimizationResults.summary.fleet_balance.fleet_count}). No rebalancing needed.
                        </div>
                      )
                    ) : (
                      <div>
                        Over fleet by {optimizationResults.summary.fleet_balance.overage} bus
                        {optimizationResults.summary.fleet_balance.overage === 1 ? "" : "es"}
                        ({optimizationResults.summary.fleet_balance.total_buses_after}/{optimizationResults.summary.fleet_balance.fleet_count}).
                        {optimizationResults.summary.fleet_balance.removed_buses > 0 && (
                          <> Dropped {optimizationResults.summary.fleet_balance.removed_buses} so far; remaining routes can't fit within detour threshold.</>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Fleet Usage — per-capacity available vs used, plus soft-overflow diagnostics.
                    Only shown for Constrained mode (Ideal is unbounded by design). */}
                {optimizationResults.summary.fleet_usage && optimizationResults.summary.fleet_usage.mode === "constrained" && (
                  (() => {
                    const fu = optimizationResults.summary.fleet_usage;
                    const hasDeficit = fu.deficit && Object.keys(fu.deficit).length > 0;
                    const caps = Array.from(new Set([
                      ...Object.keys(fu.available || {}),
                      ...Object.keys(fu.used || {}),
                    ])).map(Number).sort((a, b) => b - a);
                    return (
                      <div
                        className={`rounded-md border p-2.5 mb-3 text-xs ${
                          hasDeficit ? "bg-red-50 border-red-300 text-red-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"
                        }`}
                        data-testid="fleet-usage-panel"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="font-semibold">Fleet Usage</div>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider opacity-70">
                              <th className="pb-1">Capacity</th>
                              <th className="pb-1 text-right">Available</th>
                              <th className="pb-1 text-right">Used</th>
                              <th className="pb-1 text-right">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {caps.map((c) => {
                              const a = (fu.available || {})[c] ?? 0;
                              const u = (fu.used || {})[c] ?? 0;
                              const d = u - a;
                              return (
                                <tr key={c} className="font-mono">
                                  <td className="py-0.5">{c}-seater</td>
                                  <td className="py-0.5 text-right">{a}</td>
                                  <td className="py-0.5 text-right">{u}</td>
                                  <td className={`py-0.5 text-right font-semibold ${d > 0 ? "text-red-700" : d < 0 ? "text-emerald-700" : ""}`}>
                                    {d > 0 ? `+${d}` : d < 0 ? d : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {hasDeficit && (
                          <div className="mt-2 pt-2 border-t border-red-200 space-y-1.5">
                            <div className="font-semibold">
                              Fleet insufficient — {Object.entries(fu.deficit).map(([c, n]) => `+${n} × ${c}-seater`).join(", ")} needed.
                            </div>
                            <div className="text-[11px] opacity-80">
                              Overflow buses have been added to the plan (marked in results). Options to close the gap:
                            </div>
                            <ul className="text-[11px] list-disc list-inside space-y-0.5 opacity-90">
                              <li>Add the missing buses to your fleet and re-run.</li>
                              {(fu.merge_hints || []).slice(0, 3).map((h, i) => (
                                <li key={i}>
                                  Merge <b>{h.from_zone_name}</b> into <b>{h.into_zone_name}</b>
                                  {" "}(adds ~{h.extra_drive_min} min drive time, {h.distance_km} km).
                                </li>
                              ))}
                              <li>Accept the overflow (already applied — see Δ column above).</li>
                            </ul>
                          </div>
                        )}
                        {!hasDeficit && fu.available && Object.keys(fu.available).length > 0 && (
                          <div className="mt-1.5 text-[11px] opacity-80">
                            Every bus in the plan comes from your uploaded fleet — no overflow.
                          </div>
                        )}
                        {/* Repair pass — post-optimization downgrade actions */}
                        {(optimizationResults.summary.fleet_repair_actions || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                            <div className="font-semibold text-[11px] text-slate-700">
                              Auto-repair swaps ({optimizationResults.summary.fleet_repair_actions.length})
                            </div>
                            <ul className="text-[11px] list-disc list-inside space-y-0.5 text-slate-600" data-testid="fleet-repair-list">
                              {optimizationResults.summary.fleet_repair_actions.slice(0, 5).map((a, i) => (
                                <li key={i}>
                                  <b>{a.zone_name}</b>: 1×{a.victim_capacity}-seater @{a.victim_utilization_pct}% util
                                  {" → "}
                                  {a.new_mix.map(m => `${m.count}×${m.capacity}`).join(" + ")}
                                </li>
                              ))}
                              {optimizationResults.summary.fleet_repair_actions.length > 5 && (
                                <li className="opacity-70">
                                  + {optimizationResults.summary.fleet_repair_actions.length - 5} more…
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* Results List */}
                <div className="space-y-3">
                  {optimizationResults.results.map((result) => {
                    const isGroup = result.entity_type === "group";
                    const group = isGroup ? groups.find((g) => g.id === result.entity_id) : null;
                    const hasRoutes = result.bus_routes && result.bus_routes.length > 0;
                    return (
                      <div
                        key={result.entity_id}
                        className="border rounded-md p-3"
                        data-testid={`result-${result.entity_id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {isGroup && group && (
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: group.color }}
                            />
                          )}
                          <span className="font-medium text-sm truncate">
                            {result.entity_name}
                          </span>
                          <Badge variant="outline" className="ml-auto text-xs">
                            {isGroup ? "Group" : "Stop"}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                          {Object.entries(result.shift_demands || {}).sort(([a], [b]) => a.localeCompare(b)).map(([sk, val]) => (
                            <div key={sk}>
                              <span className="text-muted-foreground">{sk}: </span>
                              <span className="font-mono">{val}</span>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs mb-2">
                          <span className="text-muted-foreground">Carried: </span>
                          <span className="font-mono font-semibold" data-testid={`result-carried-${result.entity_id}`}>
                            {(result.bus_routes || []).reduce((acc, br) => acc + (br.total_passengers || 0), 0)}
                          </span>
                          {(() => {
                            // Show original peak alongside for context whenever
                            // anchor absorption has moved demand between zones.
                            const carried = (result.bus_routes || []).reduce(
                              (acc, br) => acc + (br.total_passengers || 0), 0
                            );
                            const orig = result.peak_demand || 0;
                            if (orig !== carried && orig > 0) {
                              return (
                                <span className="text-muted-foreground text-[10px] ml-1">
                                  (peak: {orig})
                                </span>
                              );
                            }
                            return null;
                          })()}
                          <span className="text-muted-foreground ml-2">Routes: </span>
                          {result.allocated_buses.map((bus, i) => (
                            <span key={i} className="bus-badge">
                              {bus.count}x{bus.capacity}
                            </span>
                          ))}
                          {result.vehicle_schedule && (
                            <span className="ml-2">
                              <span className="text-muted-foreground">Vehicles: </span>
                              <span className="font-mono font-semibold">{result.vehicle_schedule.total_vehicles}</span>
                            </span>
                          )}
                        </div>
                        {hasRoutes && (
                          <div className="mt-2 pt-2 border-t">
                            <div className="flex items-center gap-2 mb-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-8 px-2"
                                onClick={() => {
                                  setSelectedRouteEntity(result);
                                  setShowRoutesPanel(true);
                                }}
                                data-testid={`view-routes-${result.entity_id}`}
                              >
                                <Table className="w-3 h-3 mr-1" />
                                View Routes
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {result.bus_routes.map((br) => (
                                <Button
                                  key={br.bus_id}
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs h-6 px-1.5 font-mono hover:bg-blue-50"
                                  onClick={() => viewRouteOnMap(br, result)}
                                  data-testid={`map-route-${br.bus_id}`}
                                  title={`View ${br.bus_id} on map`}
                                >
                                  <MapPin className="w-3 h-3 mr-0.5 text-blue-500" />
                                  {br.bus_id}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Group Name Dialog - High z-index to appear above map */}
        <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
          <DialogContent className="z-[9999]" style={{ zIndex: 9999 }} data-testid="group-dialog">
            <DialogHeader>
              <DialogTitle>Create Group</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium mb-2 block">Group Name</label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter group name"
                data-testid="group-name-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                {selectedStopIds.size} stops will be added to this group
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateGroup} data-testid="create-group-btn">
                Create Group
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Office Location Dialog */}
        <Dialog open={showOfficeDialog} onOpenChange={setShowOfficeDialog}>
          <DialogContent className="z-[9999]" style={{ zIndex: 9999 }} data-testid="office-dialog">
            <DialogHeader>
              <DialogTitle>Set Office Location</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Office Name</label>
                <Input
                  value={officeForm.name}
                  onChange={(e) => setOfficeForm({ ...officeForm, name: e.target.value })}
                  placeholder="e.g., Main Office"
                  data-testid="office-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Latitude</label>
                  <Input
                    type="number"
                    step="any"
                    value={officeForm.latitude}
                    onChange={(e) => setOfficeForm({ ...officeForm, latitude: e.target.value })}
                    placeholder="e.g., 12.9716"
                    data-testid="office-lat-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Longitude</label>
                  <Input
                    type="number"
                    step="any"
                    value={officeForm.longitude}
                    onChange={(e) => setOfficeForm({ ...officeForm, longitude: e.target.value })}
                    placeholder="e.g., 77.5946"
                    data-testid="office-lng-input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                The office location will be displayed as a special marker and cannot be grouped with stops.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOfficeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSetOffice} data-testid="save-office-btn">
                <Building2 className="w-4 h-4 mr-2" />
                Set Location
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bus Routes Table Dialog */}
        <Dialog open={showRoutesPanel} onOpenChange={setShowRoutesPanel}>
          <DialogContent className="z-[9999] max-w-4xl max-h-[80vh] overflow-hidden" style={{ zIndex: 9999 }} data-testid="routes-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bus className="w-5 h-5" />
                Bus Routes - {selectedRouteEntity?.entity_name}
              </DialogTitle>
            </DialogHeader>
            <RoutesModalContent 
              selectedRouteEntity={selectedRouteEntity} 
              setShowRoutesPanel={setShowRoutesPanel}
              setSelectedRouteEntity={setSelectedRouteEntity}
            />
          </DialogContent>
        </Dialog>

        <AssignBusesModal
          open={showAssignBuses}
          onClose={setShowAssignBuses}
          optimizationResults={optimizationResults}
          stops={stops}
          individualVehicles={individualVehicles}
          assignments={busAssignments}
          setAssignments={setBusAssignments}
        />

        {/* Confirmation modal: destructive Fresh Run switch when clusters exist */}
        <Dialog open={!!pendingClusterModeChange} onOpenChange={(o) => { if (!o) setPendingClusterModeChange(null); }}>
          <DialogContent className="max-w-md" data-testid="fresh-run-confirm-modal">
            <DialogHeader>
              <DialogTitle>Discard manual cluster edits?</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-2">
              <p>
                Switching to <b>Fresh Run</b> will recompute clusters from scratch on the next
                <b> Generate Routes</b> — any manual zone assignments will be lost.
              </p>
              <p className="text-slate-500 text-xs">
                Choose <b>Cancel</b> to stay on Last Saved.
              </p>
            </div>
            <DialogFooter className="mt-4 gap-2">
              <Button
                variant="outline"
                onClick={() => setPendingClusterModeChange(null)}
                data-testid="fresh-run-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setClusterMode(pendingClusterModeChange);
                  setHasOptimized(false);
                  setPendingClusterModeChange(null);
                }}
                data-testid="fresh-run-confirm"
              >
                Yes, switch to Fresh Run
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Warning modal: >10% of demand-stops are newly-added under Last Saved */}
        <Dialog open={!!manyNewStopsWarning} onOpenChange={(o) => { if (!o) setManyNewStopsWarning(null); }}>
          <DialogContent className="max-w-md" data-testid="many-new-stops-modal">
            <DialogHeader>
              <DialogTitle>Many new stops detected</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-2">
              <p>
                <span className="font-semibold">{manyNewStopsWarning?.new_count}</span> of{" "}
                <span className="font-semibold">{manyNewStopsWarning?.demand_count}</span> stops
                with demand ({Math.round((manyNewStopsWarning?.ratio || 0) * 100)}%) have no zone assignment.
              </p>
              <p>
                Under <b>Last Saved</b>, they will be auto-assigned to the nearest existing zone.
                This may distort zone capacities.
              </p>
              <p className="text-slate-500 text-xs">
                Recommendation: switch to <b>Fresh Run</b> for a clean recluster, or cancel and
                manually assign the new stops from the map first.
              </p>
            </div>
            <DialogFooter className="mt-4 gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setManyNewStopsWarning(null)}
                data-testid="many-new-stops-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setClusterMode("fresh_run");
                  setHasOptimized(false);
                  setManyNewStopsWarning(null);
                  // Immediately re-fire optimize with fresh_run (acknowledged bypass)
                  setTimeout(() => handleOptimize({ acknowledgedManyNewStops: true }), 0);
                }}
                data-testid="many-new-stops-switch-fresh"
              >
                Switch to Fresh Run & continue
              </Button>
              <Button
                onClick={() => {
                  setManyNewStopsWarning(null);
                  handleOptimize({ acknowledgedManyNewStops: true });
                }}
                data-testid="many-new-stops-proceed"
              >
                Continue with Last Saved
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <VehicleChainsTable
          open={showVehicleChains}
          onOpenChange={setShowVehicleChains}
          optimizationResults={optimizationResults}
        />

        <Toaster position="bottom-right" richColors />
      </div>
    </TooltipProvider>
  );
}

export default App;
